/**
 * English message catalog — source of truth for short UI strings in this slice.
 * Keys align with the i18n manifest / uiStrings / stepActionTemplates.
 */

export type MessageCatalog = Record<string, string>;

export const enCatalog: MessageCatalog = {
  // Shared step buttons
  'ui.READ_GREEN_BOOK': 'Read Green Book',
  'ui.READ_GUIDANCE': 'Read Guidance',
  'ui.WATCH_VIDEO': 'Watch Video',
  'ui.SHARE_WITH_SPONSOR': 'Share with sponsor',
  'ui.FIRST_STEP_WRITING_WORKSPACE': 'First Step writing workspace',
  'ui.OPEN_AMENDS_LIST': 'Open Amends List',
  'ui.PRACTICE_INVENTORY': 'Practice Inventory',
  'ui.ENTER_UPDATE_3_CIRCLES': 'Enter/update my 3 Circles',
  'ui.SHARE_ACTION_TITLE': 'Share and review your work with your sponsor',
  'ui.SHARE_DEFECTS_ACTION_TITLE': 'Share and review my character defects with my sponsor',
  'ui.DEFAULT_SHARE_EMPTY_MESSAGE':
    'Complete at least one action or add some work on this step before sharing.',

  // Step name ordinals for {stepName}
  'stepName.1': 'One',
  'stepName.2': 'Two',
  'stepName.3': 'Three',
  'stepName.4': 'Four',
  'stepName.5': 'Five',
  'stepName.6': 'Six',
  'stepName.7': 'Seven',
  'stepName.8': 'Eight',
  'stepName.9': 'Nine',
  'stepName.10': 'Ten',
  'stepName.11': 'Eleven',
  'stepName.12': 'Twelve',

  // Title templates
  'step.action.template.how_to_work.title': 'How to work Step {stepName} (guidance & video)',
  'step.action.template.read_guidance.title': 'Read guidance on working Step {stepName}',
  'step.action.template.read_green_book.title': 'Read the SAA Green Book about Step {stepName}',
  'step.action.template.understand_step.title':
    "Understand what Step {stepName} in SAA is about and why it's important",

  // Description templates
  'step.action.template.guidance_and_video.description':
    'Read guidance and watch video on how to work through Step {stepName}',
  'step.action.template.practical_guidance.description':
    'Read practical guidance on how to work Step {stepName} effectively.',
  'step.action.template.review_with_sponsor.description':
    "Review your Step {stepName} work with your sponsor. You can check this off when you've shared or discussed your work — using the share button is optional.",

  // Language offer / switcher chrome (English for now; admin UIs stay EN but this is user-facing)
  'i18n.offer.title': 'Language preference',
  'i18n.offer.body': 'It looks like you prefer {language}. Would you like to use it on Recovery Commons?',
  'i18n.offer.accept': 'Use {language}',
  'i18n.offer.decline': 'Keep English',
  'i18n.auto.body': "We're showing Recovery Commons in {language} because your browser is set to {language}.",
  'i18n.auto.keep': 'Continue in {language}',
  'i18n.switcher.label': 'Language',
  'i18n.switcher.aria': 'Choose language',
  'i18n.switcher.preview': 'preview',

  // Getting Started unique titles (action 7 = ui.SHARE_ACTION_TITLE)
  'getting_started.action.1.title': 'Read about our addiction',
  'getting_started.action.2.title': 'Read about our fellowship',
  'getting_started.action.3.title': 'My sponsor',
  'getting_started.action.4.title': 'Read about the 3 Circles',
  'getting_started.action.5.title': 'Watch: The 3 Circles of SAA',
  'getting_started.action.6.title': 'Define my 3 Circles',
  'getting_started.action.8.title': 'Ready for Step One',

  // Unique step action titles (templated actions 1–2 and shared share titles are elsewhere)
  'step.1.action.3.title': 'Prepare my First Step story',
  'step.1.action.4.title': 'Talk with my sponsor about my First Step',
  'step.1.action.5.title': 'Read my First Step in a meeting',
  'step.1.action.6.title':
    'Take Step One: Admit that I am powerless over compulsive sexual addiction - that my life has become unmanageable',
  'step.2.action.3.title': 'Answer Step Two questions',
  'step.2.action.5.title': 'Take Step Two: Declare willingness',
  'step.3.action.3.title': 'Answer Step Three questions',
  'step.3.action.4.title': 'Read the Third Step Prayer — and write my own if I choose',
  'step.3.action.6.title': 'Take Step Three',
  'step.4.action.3.title': 'Fill out inventories and schedule Step Five',
  'step.4.action.5.title':
    'Take Step Four: I have made a searching and fearless moral inventory of myself',
  'step.5.action.3.title': 'Present my Step Four inventories to my sponsor',
  'step.5.action.4.title': 'Identify my character defects',
  'step.5.action.6.title': 'Take Step Five',
  'step.6.action.3.title': 'Work through character defects',
  'step.6.action.5.title':
    'Take Step Six: Were entirely ready to have God remove all these defects of character',
  'step.7.action.3.title': 'Read the Seventh Step Prayer — and write my own if I choose',
  'step.7.action.5.title': 'Take Step Seven: read the Seventh Step Prayer aloud',
  'step.8.action.3.title': 'Take Step Eight: Make a list of persons I have harmed',
  'step.8.action.5.title':
    'Take Step Eight: Made a list of all persons we had harmed, and became willing to make amends to them all',
  'step.9.action.3.title': 'Take Step Nine: Make direct amends to such people wherever possible',
  'step.9.action.5.title':
    'Take Step Nine: Made direct amends to such people wherever possible, except when to do so would injure them or others',
  'step.10.action.3.title':
    'Take Step Ten: Continue to take personal inventory and promptly admit when wrong',
  'step.10.action.5.title':
    'Take Step Ten: Continued to take personal inventory and when we were wrong promptly admitted it',
  'step.11.action.3.title': 'Define my daily quiet time plan and practice it',
  'step.11.action.5.title': 'Take Step Eleven',
  'step.12.action.3.title': 'Define my service plan',
  'step.12.action.5.title': 'Take Step Twelve',

  // Nav / shared step chrome
  'nav.home': 'Home',
  'nav.saa': 'SAA',
  'nav.12_steps': '12 Steps',
  'ui.COMPLETED': 'Completed',
  'ui.EXPAND': 'Expand',
  'ui.COLLAPSE': 'Collapse',
  'step.page.title': 'Step {stepName}',
  'step.page.actions_title': 'Actions to Complete Step {stepName}',
  'step.page.actions_intro':
    "Complete each action in order. Check the box when you've finished each action.",

  'getting_started.page.title': 'Getting Started',
  'getting_started.page.subtitle':
    'Understand my addiction and define what acting out means for me — before beginning Step One.',
  'getting_started.page.actions_title': 'Actions to Complete Getting Started',

  // Traditional step quotes
  'step.1.quote':
    'We admitted we were powerless over compulsive sexual behavior and that our lives had become unmanageable.',
  'step.2.quote': 'Came to believe that a Power greater than ourselves could restore us to sanity.',
  'step.3.quote':
    'Made a decision to turn our will and our lives over to the care of God as we understood Him.',
  'step.4.quote': 'Made a searching and fearless moral inventory of ourselves.',
  'step.5.quote':
    'Admitted to God, to ourselves, and to another human being the exact nature of our wrongs.',
  'step.6.quote': 'Were entirely ready to have God remove all these defects of character.',
  'step.7.quote': 'Humbly asked Him to remove our shortcomings.',
  'step.8.quote':
    'Made a list of all persons we had harmed, and became willing to make amends to them all.',
  'step.9.quote':
    'Made direct amends to such people wherever possible, except when to do so would injure them or others.',
  'step.10.quote':
    'Continued to take personal inventory and when we were wrong promptly admitted it.',
  'step.11.quote':
    'Sought through prayer and meditation to improve our conscious contact with God as we understood Him, praying only for knowledge of His will for us and the power to carry that out.',
  'step.12.quote':
    'Having had a spiritual awakening as the result of these steps, we tried to carry this message to sex addicts and to practice these principles in all our affairs.',

  // Getting Started descriptions
  'getting_started.action.1.description': 'Introduction and Our Addiction from the Green Book',
  'getting_started.action.2.description': 'Meetings and sponsorship from the Green Book',
  'getting_started.action.3.description':
    'To effectively work the steps, I need to have a sponsor. Where are you in that process?',
  'getting_started.action.4.description':
    'How SAA members define abstinence using the Three Circles tool',
  'getting_started.action.5.description': 'Video diving deeper into this valuable tool',
  'getting_started.action.6.description':
    'Enter/update my personal 3 circles: inner (acting out), middle (warning signs), and outer (supports recovery). I realize that these can change as I go through recovery and that I will only make changes after talking with my sponsor and others in the program.',
  'getting_started.action.7.description':
    "Review your Getting Started progress and 3 Circles with your sponsor. You can check this off when you've shared or discussed your work — using the share button is optional.",
  'getting_started.action.8.description':
    'You have completed the introductory work and are ready to begin Step One.',

  // Getting Started — sponsor action (action 3)
  'getting_started.sponsor.have_sponsor': 'I have a sponsor',
  'getting_started.sponsor.looking': "I'm looking for a sponsor",
  'getting_started.sponsor.ask_in_meeting': "I'll ask someone in a meeting",
  'getting_started.sponsor.open_profile_cta': 'Open Profile → Sponsorship',
  'getting_started.sponsor.added_one':
    "You have added your sponsor's name and email on your profile.",
  'getting_started.sponsor.added_many':
    "You have added your sponsors' names and emails on your profile.",

  // Getting Started — 3 Circles workspace (modal + preview)
  'getting_started.circles.modal_title': 'My 3 Circles',
  'getting_started.circles.close': 'Close',
  'getting_started.circles.login_prompt':
    'Please sign in to create and manage your 3 Circles.',
  'getting_started.circles.intro':
    'Define your personal 3 Circles. Inner = acting out for you. Middle = warning signs. Outer = what supports recovery. Talk them through with your sponsor when you can.',
  'getting_started.circles.inner.title': 'Inner circle',
  'getting_started.circles.inner.subtitle': 'Behaviors I identify as acting out',
  'getting_started.circles.inner.placeholder': 'Add a behavior',
  'getting_started.circles.inner.name': 'inner',
  'getting_started.circles.middle.title': 'Middle circle',
  'getting_started.circles.middle.subtitle': 'Slippery slope — warning signs',
  'getting_started.circles.middle.placeholder': 'Add a warning sign',
  'getting_started.circles.middle.name': 'middle',
  'getting_started.circles.outer.title': 'Outer circle',
  'getting_started.circles.outer.subtitle': 'Healthy — supports my recovery',
  'getting_started.circles.outer.placeholder': 'Add a healthy behavior',
  'getting_started.circles.outer.name': 'outer',
  'getting_started.circles.add_to_list': 'Add to List',
  'getting_started.circles.remove': 'Remove',
  'getting_started.circles.remove_confirm': 'Remove "{item}" from your {circle} circle?',
  'getting_started.circles.saving': 'Saving…',
  'getting_started.circles.unsaved': 'Unsaved changes',
  'getting_started.circles.saved_encrypted': 'Saved · encrypted',
  'getting_started.circles.last_saved': ' · Last saved {time}',
  'getting_started.circles.preview.inner':
    'Inner circle — behaviors I identify as acting out',
  'getting_started.circles.preview.middle': 'Middle — warning signs',
  'getting_started.circles.preview.outer': 'Outer — supports recovery',

  'getting_started.video.title': 'The 3 Circles of SAA',
  'getting_started.share.empty_message':
    'Complete at least one action or add to your 3 Circles before sharing.',

  // Unique action descriptions
  'step.1.action.1.description':
    'Understand how my addictive behavior made life unmanageable and why surrender matters',
  'step.1.action.3.description':
    "Draft or update your First Step in the writing workspace. View, download, or share your story when you're ready.",
  'step.1.action.4.description':
    "It's important that I review my First Step with a sponsor or other trusted person before presenting it.",
  'step.1.action.5.description': 'Share your Step One work in an SAA meeting',
  'step.1.action.6.description': 'Make the final admission that completes Step One',

  'step.2.action.1.description':
    'Discover how belief in a power greater than myself restored sanity for others-and how it can for me',
  'step.2.action.3.description':
    "Reflect honestly on spirituality, higher power, and related experiences — there's no right answer, just your story.",
  'step.2.action.5.description':
    'I am willing to believe that a power greater than myself can restore me to sanity',

  'step.3.action.1.description':
    'Learn how turning life and will over to spiritual care opens the door to my transformation',
  'step.3.action.3.description':
    "Reflect on self-will, turning your life over, and your higher power — there's no right answer, only honest ones.",
  'step.3.action.4.description':
    'Read the traditional Third Step Prayer below. Writing your own version is optional — many members do.',
  'step.3.action.5.description':
    "Share your Step Three questions and prayer (if you wrote your own) with your sponsor. You can check this off when you've shared or discussed your work — using the share button is optional.",
  'step.3.action.6.description':
    'I have made a decision to turn my will and my life over to the care of God as I understand God.',

  'step.4.action.1.description':
    'Learn how writing fearless moral inventories can help me uncover how beliefs and feelings drive my behavior',
  'step.4.action.3.description':
    'Facing my resentments, fears, and harms is how real healing begins.',
  'step.4.action.5.description': 'Complete the step by acknowledging your inventory work',

  'step.5.action.1.description':
    'Discover why revealing the exact nature of my wrongs brings release and clarity',
  'step.5.action.3.description':
    "Sit down with your sponsor in person or online and go through your inventories out loud — entry by entry. Your sponsor listens, encourages, and may take notes. Some members need several meetings to finish. You and your sponsor may work this and the next 2 actions in this step in whatever order fits your process. Check this off when you've completed presenting your inventories (or when you're satisfied with where you are for now).",
  'step.5.action.4.description':
    "With your sponsor's help, name the character defects that showed up in your inventory. You may notice many defects, but most members focus on their 3–6 most impactful ones for this pass through the steps. Enter your initial list here - we will work on these more in Steps Six and Seven.",
  'step.5.action.5.description':
    "If you and your sponsor haven't already agreed on the character defects you'll work on this time through the steps, update your sponsor on your list. Check this off when you've discussed it — the share button is optional for a written summary.",
  'step.5.action.6.description':
    'Admitted to God, to myself, and to another human being the exact nature of my wrongs.',

  'step.6.action.1.description':
    "Grasp what 'entire readiness' truly means-and how it prepares me deeply for change",
  'step.6.action.3.description':
    'Answer questions about each character defect to become ready to let them go',
  'step.6.action.5.description':
    'Complete Step Six by acknowledging your readiness to let go of character defects',

  'step.7.action.1.description':
    'Explore how humble prayer can unlock removal of my shortcomings and invite spiritual freedom',
  'step.7.action.3.description':
    'Read the traditional Seventh Step Prayer below. Writing your own version is optional — many members do.',
  'step.7.action.4.description':
    "Share your Seventh Step Prayer (if you wrote your own) with your sponsor. You can check this off when you've shared or discussed your work — using the share button is optional.",
  'step.7.action.5.description':
    'Humbly ask your Higher Power to remove your shortcomings by reading the Seventh Step Prayer aloud — using your own words or the traditional version — and return to it regularly.',

  'step.8.action.1.description':
    "Learn why naming whom I've harmed and building willingness are critical before making amends",
  'step.8.action.3.description': 'Create a list of people you have harmed through your actions',
  'step.8.action.5.description': 'Complete Step Eight by acknowledging your amends list work',

  'step.9.action.1.description':
    'Understanding how to make amends in ways that heal-without causing new harm',
  'step.9.action.3.description':
    'Make amends to people you have harmed, except when to do so would injure them or others',
  'step.9.action.5.description': 'Complete Step Nine by acknowledging your amends work',

  'step.10.action.1.description':
    'Discover how daily personal inventory and prompt admission sustain clarity and connection in my recovery',
  'step.10.action.3.description':
    'Practice ongoing self-examination and admit mistakes promptly',
  'step.10.action.5.description': 'Complete Step Ten by acknowledging your inventory practice',

  'step.11.action.1.description':
    'Learn how prayer and meditation deepen my conscious contact and guide meaningful action',
  'step.11.action.3.description':
    'Write out your plan for prayer and meditation, then practice it in your daily routine.',
  'step.11.action.4.description':
    "Share your quiet time plan with your sponsor. You can check this off when you've shared or discussed your work — using the share button is optional.",
  'step.11.action.5.description':
    'Sought through prayer and meditation to improve our conscious contact with God as we understood Him, praying only for knowledge of His will for us and the power to carry that out.',

  'step.12.action.1.description':
    'See how spiritual awakening naturally leads me to carry the message and live recovery in all areas of life',
  'step.12.action.3.description':
    'Write out how you want to carry the message and practice these principles in your daily life.',
  'step.12.action.4.description':
    "Share your service plan with your sponsor. You can check this off when you've shared or discussed your work — using the share button is optional.",
  'step.12.action.5.description':
    'Having had a spiritual awakening as the result of these steps, we tried to carry this message to sex addicts and to practice these principles in all our affairs.',

  'ui.REFRESH_PAGE': 'Refresh Page',
  'step.page.loading': 'Loading your progress...',
  'step.page.congrats_heading': '🎉 Congratulations!',
  'step.page.congrats_body':
    'You have completed all actions for Step {stepName} and can move on to Step {nextStepName}.',
  'step.page.prev_link': '<< Step {stepName}',
  'step.page.next_link': 'Step {stepName} >>',
  'step.page.prev_getting_started': '<< Getting Started',
  'step.page.guidance_title': 'Guidance on working Step {stepName}',
  'step.page.video_title': 'Step {stepName} Video Guide',

  'getting_started.page.congrats_body':
    'You have completed Getting Started and can begin Step One.',
  'getting_started.page.next_link': 'Step One >>',
  'getting_started.page.congrats_heading': 'Congratulations!',

  'step.12.congrats_body':
    '🎉 Congratulations! You have completed all 12 Steps of SAA. You are now ready to continue your recovery journey and help others.',

  'step.8.helper.empty':
    'No persons added yet. Click "Open Amends List" to get started.',
  'step.8.helper.count':
    '{count} {personWord} on your list ({completed} completed).',
  'step.8.helper.person': 'person',
  'step.8.helper.persons': 'persons',
  'step.8.helper.requires_action3':
    'Action 4 requires Action 3 to be completed first.',

  'step.9.helper.copy_from_step8':
    'You can copy your list from Step 8. Click "Open Amends List" to open the dialog, then use the "Copy from Step Eight" button to bring forward your Step 8 list. You can then add your amends plan and track your progress making amends.',
  'step.9.helper.count':
    '{count} {personWord} on your amends list ({planned} planned, {completed} completed).',

  'step.10.helper.empty':
    "Practice taking a nightly inventory when you're ready. Click \"Practice Inventory\" to add entries — or check off this action anytime; there's no minimum number of entries.",
  'step.10.helper.count_complete': '{count} {entryWord} complete',
  'step.10.helper.count_incomplete': '({count} incomplete).',
  'step.10.helper.entry': 'entry',
  'step.10.helper.entries': 'entries',

  // Shared question workspace chrome (Steps 2–3 deep Q&A)
  'step.question.placeholder_default': 'Start typing your answer here...',
  'step.question.help_button_title': 'Get help with this question',
  'step.question.help_dialog_title': 'Help',
  'step.question.help_not_found': 'Help content not found for this question.',
  'step.question.help_error': 'Error loading help content.',
  'step.workspace.saving': 'Saving...',
  'step.workspace.saved': '✓ Saved',
  'step.workspace.saved_all': '✓ All changes saved',
  'step.workspace.save_failed': '⚠ Save failed. Please try again.',
  'step.workspace.save_answers_error': 'Failed to save answers. Please try again.',
  'step.workspace.save_prayer_error': 'Failed to save prayer. Please try again.',

  // Step Two questions
  'step.2.question.q1.label':
    'Describe what spirituality / religion looked like for you growing up, its impact on you, and any feelings you have about it.',
  'step.2.question.q1.placeholder':
    'e.g., In my family, religion was strict and scary. It made me feel disconnected...',
  'step.2.question.q2.label':
    'Describe the role, if any, that spirituality / religion has in your life currently.',
  'step.2.question.q2.placeholder':
    "e.g., Right now, I think of spirituality as something I'm exploring. I'm open to...",
  'step.2.question.q3.label':
    'Describe your higher power if you have one (e.g., perfectionist judge, kind grandpa, vending machine, not there)',
  'step.2.question.q3.placeholder':
    "e.g., My higher power is the group and the connection I feel there. It's not religious but...",
  'step.2.question.q4.label':
    'Describe any feelings of anger you had or have about a higher power.',
  'step.2.question.q4.placeholder':
    "e.g., I felt angry when my prayers weren't answered. I thought a higher power should have...",
  'step.2.question.q5.label':
    'Recount any times you have prayed for something or made a deal with your higher power in moments of distress, and what happened after.',
  'step.2.question.q5.placeholder':
    'e.g., One time I prayed when I was desperate to stop acting out. I told God I would...',
  'step.2.question.q6.label':
    'If appropriate, describe how someone in your life abused their authority over you and how that impacted your view of a higher power.',
  'step.2.question.q6.placeholder':
    'e.g., Someone in my life had control over me when I was young. It made it hard for me to trust...',

  // Step Three questions
  'step.3.question.q1.label':
    'Who or what made my decisions for me while I was compulsively sexually acting out?',
  'step.3.question.q1.placeholder':
    "e.g., My cravings and urges made all the decisions. I didn't feel like I had a choice...",
  'step.3.question.q2.label':
    'What fears do I have about making a decision to turn my will and life over to a higher power of my understanding?',
  'step.3.question.q2.placeholder':
    "e.g., I'm afraid nothing will change and I'll be disappointed...",
  'step.3.question.q3.label': 'Which problems have I tried to resolve through willpower?',
  'step.3.question.q3.placeholder':
    'e.g., I tried to quit acting out by making promises and tracking behavior...',
  'step.3.question.q4.label':
    'How has self-will and my reactions to life events made me and others suffer? Be specific.',
  'step.3.question.q4.placeholder':
    'e.g., I lashed out at my partner when I felt ashamed...',
  'step.3.question.q5.label': 'Who or what makes my decisions for me in recovery?',
  'step.3.question.q5.placeholder':
    'e.g., I pause and ask my Higher Power what the next right thing is...',
  'step.3.question.q6.label':
    'Is my current concept of a higher power still working, and how might I need to change my concept of a higher power?',
  'step.3.question.q6.placeholder':
    'e.g., My concept used to be punishing. Now I see my Higher Power as kind and steady...',
  'step.3.question.q7.label':
    'What are specific examples of how I turned my life and will over to my addiction?',
  'step.3.question.q7.placeholder':
    'e.g., I skipped work and lied to my partner just to act out...',
  'step.3.question.q8.label':
    'How would my day look if I were to turn my will and my life over to the care of a higher power of my understanding?',
  'step.3.question.q8.placeholder': "e.g., I'd feel calmer and more present...",
  'step.3.question.q9.label':
    'Which thoughts and behaviors am I willing to turn over right now, and which am I not yet?',
  'step.3.question.q9.placeholder':
    "e.g., I'm willing to let go of trying to fix everything myself...",

  // Step Three prayer section.
  // NOTE: step.3.prayer.traditional_line* is the traditional Third Step Prayer from
  // Alcoholics Anonymous (p. 63), also used in SAA. Translations must use the published
  // Spanish AA wording — never a newly authored prayer.
  'step.3.prayer.traditional_heading': '3rd Step Prayer (from Alcoholics Anonymous, page 63)',
  'step.3.prayer.traditional_line1':
    'God, I offer myself to Thee – to build with me and to do with me as Thou wilt.',
  'step.3.prayer.traditional_line2':
    'Relieve me of the bondage of self, that I may better do Thy will.',
  'step.3.prayer.traditional_line3':
    'Take away my difficulties, that victory over them may bear witness to those I would help of Thy Power, Thy Love, and Thy Way of life.',
  'step.3.prayer.traditional_line4': 'May I do Thy will always!',
  'step.3.prayer.own_words_label': 'Third Step Prayer in my own words',
  'step.3.prayer.own_words_placeholder':
    'Write your own version of the Third Step Prayer here...',

  // Workshop 2026 — page chrome + About tab (first pass)
  'workshop.2026.nav_label': '2026 Step Workshop',
  'workshop.2026.default_name': '2026 SAA Step Workshop',
  'workshop.2026.loading': 'Loading workshop…',
  'workshop.2026.loading_schedule': 'Loading schedule…',
  'workshop.2026.loading_details': 'Loading workshop details…',
  'workshop.2026.hero_subtitle':
    'Personally work your SAA 12 steps in community with others · Online',
  'workshop.2026.starting': 'Starting {date}',
  'workshop.2026.status_confirmed': "You're confirmed for the 2026 SAA Workshop.",
  'workshop.2026.status_first_session': 'First session: {date}.',
  'workshop.2026.tab_about': 'About',
  'workshop.2026.tab_workshop': 'Workshop',

  'workshop.2026.about.interest_banner':
    "You're on the interest list for the 2026 workshop. When you're ready to commit, confirm you're planning to attend on the Workshop tab — then the Zoom link will appear there.",
  'workshop.2026.about.see_workshop_tab': 'See Workshop tab →',
  'workshop.2026.about.confirmed_banner':
    "You're confirmed for the 2026 SAA Workshop",
  'workshop.2026.about.first_session_suffix': ' · First session: {date}',
  'workshop.2026.about.zoom_on_workshop_tab': 'The Zoom link and workshop home are on the',
  'workshop.2026.about.workshop_tab': 'Workshop tab',

  'workshop.2026.about.what_heading': 'What is a Step Workshop?',
  'workshop.2026.about.what_p1':
    'An SAA Step Workshop is a group of recovering addicts who get together regularly to work through the 12 steps of SAA and encourage each other. We meet weekly at the same time online via Zoom. Workshops typically run 35–60 weeks, depending on group pace and consensus.',
  'workshop.2026.about.what_p2':
    "Recovery often begins in isolation, but healing happens in connection. Working the steps alongside others gives us structure, encouragement, momentum, and the shared experience of people who truly understand what we're going through. Hearing others share honestly helps us see ourselves more clearly and reminds us that we don't have to recover alone. What might take years alone can often be accomplished in months when we walk the path together.",

  'workshop.2026.about.for_me_heading': 'Is this workshop for me?',
  'workshop.2026.about.for_me_li1': 'Willing to work the 12 steps of SAA',
  'workshop.2026.about.for_me_li2': 'Can commit to weekly attendance (life happens; we understand)',
  'workshop.2026.about.for_me_li3': 'Comfortable with online meetings',
  'workshop.2026.about.for_me_li4': 'No prior step work required to start',
  'workshop.2026.about.for_me_p1':
    'This workshop is open to anyone seeking recovery from compulsive sexual behavior — regardless of gender, sexual orientation, gender identity, relationship status, or background. The only requirement is a desire to stop addictive sexual behavior.',
  'workshop.2026.about.for_me_p2':
    'You are also welcome and encouraged to join us if you have worked some or all of the steps before — to complete the steps, work them again, help others, deepen your recovery, or share your wisdom.',
  'workshop.2026.about.for_me_sponsor':
    "You'll need a sponsor to complete Step One. If you don't have one yet, you can still join us for the first few meetings while you look for one — but you will need a sponsor before completing Step One.",

  'workshop.2026.about.week_heading': 'What happens each week?',
  'workshop.2026.about.week_li1': 'Step-focused reading together, sharing, and encouragement',
  'workshop.2026.about.week_li2':
    'Work together through Getting Started, the 3 Circles, and steps 1 through 12',
  'workshop.2026.about.week_li3':
    'Between workshop meetings, members track step work in Recovery Commons — inventories, progress, and sharing with a sponsor',

  'workshop.2026.about.reg_heading': 'How registration works',
  'workshop.2026.about.reg_li1':
    'Create a free Recovery Commons account (or sign in if you already have an account)',
  'workshop.2026.about.reg_li2':
    "Join the interest list on the Workshop tab above - we'll keep you posted via email",
  'workshop.2026.about.reg_li3': "When you're ready, tap \"I'm planning to attend\"",
  'workshop.2026.about.reg_li4':
    'After you confirm, the Zoom link appears on the Workshop tab above',
  'workshop.2026.about.reg_signin_hint':
    "When you're ready, go to the Workshop tab to create an account or sign in.",

  'workshop.2026.about.share_heading': 'Share & invite',
  'workshop.2026.about.rc_heading': 'Recovery Commons step work',
  'workshop.2026.about.rc_body':
    'Members track inventories, prayers, amends lists, and other step work privately in Recovery Commons. From the Workshop tab, use Work my steps to open your SAA step checklist.',
  'workshop.2026.about.rc_help_link': 'How step work in Recovery Commons works',

  // Workshop tab — registration flow, Zoom, leave, interest survey, cards
  'workshop.2026.gate.lead':
    'Create a free Recovery Commons account to join the interest list for this workshop. Already have an account? Sign in.',
  'workshop.2026.gate.create_account': 'Create account',
  'workshop.2026.gate.sign_in': 'Sign in',
  'workshop.2026.gate.detail':
    "After you join the interest list and confirm you're planning to attend, this tab becomes your workshop home: Zoom link, calendar, phone list, and updates.",

  'workshop.2026.alert.interest_title': "You're on the interest list",
  'workshop.2026.alert.interest_body':
    'Your interest in the 2026 SAA Step Workshop has been recorded.',
  'workshop.2026.alert.interest_email_sent':
    ' We sent a confirmation email to {email} with a link back to this page.',
  'workshop.2026.alert.interest_email_keep':
    " We'll keep you informed via email at {email}.",
  'workshop.2026.alert.interest_email_generic':
    " We'll keep you informed via email.",

  'workshop.2026.dialog.confirm_title': "Confirm you're planning to attend?",
  'workshop.2026.dialog.confirm_message':
    'This lets the facilitators know you intend to join the 2026 SAA Workshop. The Zoom link will appear on this tab when it is published.',
  'workshop.2026.dialog.confirm_ok': "I'm planning to attend",
  'workshop.2026.dialog.confirm_cancel': 'Not yet',
  'workshop.2026.alert.confirmed_title': "You're confirmed",
  'workshop.2026.alert.confirmed_body':
    "You're confirmed for the 2026 SAA Step Workshop. First session: {date}.{emailNote} The Zoom link will appear on the Workshop tab when it is ready.",
  'workshop.2026.alert.confirm_error': 'Could not confirm attendance.',

  'workshop.2026.dialog.leave_title_confirmed': 'Leave the 2026 SAA Workshop?',
  'workshop.2026.dialog.leave_title_interest': 'Leave interest list?',
  'workshop.2026.dialog.leave_message_confirmed':
    'You will be removed from the participant roster and contact lists for the 2026 SAA Workshop. Leaving only affects your workshop membership — your step work in Recovery Commons stays with you. You can join again anytime.',
  'workshop.2026.dialog.leave_message_interest':
    'You will be removed from the interest list and will stop receiving updates about the 2026 SAA Workshop. You can join again anytime.',
  'workshop.2026.dialog.leave_ok_confirmed': 'Leave workshop',
  'workshop.2026.dialog.leave_ok_interest': 'Remove me',
  'workshop.2026.dialog.leave_cancel_confirmed': 'Stay registered',
  'workshop.2026.dialog.leave_cancel_interest': 'Keep me on the list',
  'workshop.2026.alert.leave_error': 'Could not remove you from the workshop.',

  'workshop.2026.tab.config_missing':
    'Workshop registration is not available yet — an admin needs to link this page to a workshop record.',
  'workshop.2026.tab.join_heading': 'Join the 2026 workshop',
  'workshop.2026.tab.join_body':
    "Join the interest list to get updates about the start date by email. When you're ready to commit, you'll confirm you're planning to attend — then the Zoom link appears here.",
  'workshop.2026.tab.join_cta': 'Join interest list',
  'workshop.2026.tab.next_step_title': 'Your next step',
  'workshop.2026.tab.on_interest_list': 'On interest list',
  'workshop.2026.tab.on_interest_list_since': 'On interest list since {date}',
  'workshop.2026.tab.confirm_checklist':
    "Confirm you're planning to attend — before first session",
  'workshop.2026.tab.zoom_checklist': 'Zoom link — available after you confirm',
  'workshop.2026.tab.confirming': 'Confirming…',
  'workshop.2026.tab.confirm_cta': "I'm planning to attend",

  'workshop.2026.card.participants': 'Workshop Participants',
  'workshop.2026.card.community': 'Workshop community',
  'workshop.2026.card.phone_list': 'Phone list',
  'workshop.2026.card.resources': 'Workshop Resources',
  'workshop.2026.card.share': 'Share & invite',
  'workshop.2026.card.calendar': 'Workshop calendar',

  'workshop.2026.zoom.heading': 'Join live · Zoom',
  'workshop.2026.zoom.locked_helper':
    "Available after you confirm you're planning to attend.",
  'workshop.2026.zoom.open': 'Open Zoom meeting',
  'workshop.2026.zoom.copy': 'Copy link',
  'workshop.2026.zoom.copied': 'Copied!',
  'workshop.2026.zoom.passphrase_hint': 'Link includes pass phrase.',
  'workshop.2026.zoom.placeholder':
    'Zoom link will appear here before the first session.',

  'workshop.2026.leave.title': 'Leave the workshop',
  'workshop.2026.leave.body_confirmed':
    'You can leave anytime. Your name will be removed from the participant roster and contact lists. Leaving only affects your workshop membership — your step work in Recovery Commons stays with you. You can rejoin later if your plans change.',
  'workshop.2026.leave.body_interest':
    'You can leave the interest list anytime and stop workshop emails. You can rejoin later if your plans change.',
  'workshop.2026.leave.btn_confirmed': 'Leave the 2026 SAA Workshop',
  'workshop.2026.leave.btn_interest': 'Leave the interest list',
  'workshop.2026.leave.removing': 'Removing…',

  'workshop.2026.interest_modal.title': 'Join interest list',
  'workshop.2026.interest_modal.subtitle':
    'A few questions so we can prepare for the 2026 SAA Workshop. Please answer all questions shown.',
  'workshop.2026.interest_modal.close': 'Close',
  'workshop.2026.interest_modal.select': 'Select…',
  'workshop.2026.interest_modal.cancel': 'Cancel',
  'workshop.2026.interest_modal.submit': 'Join interest list',
  'workshop.2026.interest_modal.submitting': 'Submitting…',
  'workshop.2026.interest_modal.error': 'Failed to register interest',

  'workshop.2026.interest_survey.saa_tenure.label':
    'How long have you been attending SAA meetings?',
  'workshop.2026.interest_survey.saa_tenure.new': "I'm new",
  'workshop.2026.interest_survey.saa_tenure.lt_1_year': '< 1 year',
  'workshop.2026.interest_survey.saa_tenure.1_3_years': '1-3 years',
  'workshop.2026.interest_survey.saa_tenure.3_10_years': '3-10 years',
  'workshop.2026.interest_survey.saa_tenure.10_plus_years': '10+ years',
  'workshop.2026.interest_survey.steps_worked.label':
    'Have you worked the SAA 12 steps before?',
  'workshop.2026.interest_survey.steps_worked.none': 'No',
  'workshop.2026.interest_survey.steps_worked.some': 'Some steps',
  'workshop.2026.interest_survey.steps_worked.all': 'All 12 steps',
  'workshop.2026.interest_survey.workshop_experience.label':
    'Have you worked the steps in a workshop or step-study group before?',
  'workshop.2026.interest_survey.workshop_experience.no': 'No',
  'workshop.2026.interest_survey.workshop_experience.yes': 'Yes',
  'workshop.2026.interest_survey.last_step_completed.label':
    'Which is the last step you completed?',
  'workshop.2026.interest_survey.last_step_completed.option': 'Step {n}',
  'workshop.2026.interest_survey.has_sponsor.label':
    'Do you currently have a sponsor?',
  'workshop.2026.interest_survey.has_sponsor.yes': 'Yes',
  'workshop.2026.interest_survey.has_sponsor.no': 'No',
  'workshop.2026.interest_survey.has_sponsor.looking': 'Looking for one',
  'workshop.2026.interest_survey.has_sponsor.helper':
    'A sponsor is required to complete Step One.',
  'workshop.2026.interest_survey.help_facilitate.label':
    'Interested in helping facilitate or support the workshop?',
  'workshop.2026.interest_survey.help_facilitate.yes': 'Yes',
  'workshop.2026.interest_survey.help_facilitate.maybe': 'Maybe later',
  'workshop.2026.interest_survey.help_facilitate.no': 'No',
  'workshop.2026.interest_survey.validation_required': 'Please answer: {question}.',

  'workshop.2026.share.lead':
    'Share this page, or print a one-page workshop flyer to share at meetings.',
  'workshop.2026.share.copy': 'Copy link to this page',
  'workshop.2026.share.copied': 'Copied!',
  'workshop.2026.share.flyer': 'View / Print flyer',

  'workshop.2026.step_work.cta': 'Work my steps',
  'workshop.2026.resources.empty': 'No workshop resources have been added yet.',
  'workshop.2026.calendar.empty_underway':
    'The step calendar will appear here once the workshop is underway.',
  'workshop.2026.calendar.empty_progress':
    'Calendar details will be posted here as the group progresses.',
  'workshop.2026.calendar.current_step': 'Current step',
  'workshop.2026.calendar.started_step': 'We started this step',
  'workshop.2026.calendar.where_we_are': 'Where we are',
  'workshop.2026.calendar.likely_complete': 'Likely complete',

  // Preferences
  'workshop.2026.preferences.card_title': 'My preferences for this workshop',
  'workshop.2026.preferences.card_hint':
    'Phone list, email list, and sponsor availability — only visible to workshop members.',
  'workshop.2026.preferences.standalone_hint':
    'You can update these anytime. Only workshop members see this information.',
  'workshop.2026.preferences.give_to_get_title': 'Sharing goes both ways — "give to get"',
  'workshop.2026.preferences.give_to_get_lead': "You'll only see what you're willing to share:",
  'workshop.2026.preferences.give_names': "See members' names only if you show your name",
  'workshop.2026.preferences.give_phones': "See members' phone numbers only if you list your phone",
  'workshop.2026.preferences.give_emails': "See members' email addresses only if you list your email",
  'workshop.2026.preferences.email_login_note':
    'Your email is your account login email — it cannot be changed in Recovery Commons.',
  'workshop.2026.preferences.name_heading': 'My name on the member list',
  'workshop.2026.preferences.name_help': "You'll see other members' names only if you show yours.",
  'workshop.2026.preferences.name_never': "Don't ever show my name",
  'workshop.2026.preferences.name_at_start': 'Show my name when the workshop starts ({startSchedule})',
  'workshop.2026.preferences.name_now': 'Show my name now (visible to other confirmed members)',
  'workshop.2026.preferences.phone_heading': 'Workshop phone list',
  'workshop.2026.preferences.phone_help':
    "Add your number to see other members' numbers. The shared list goes live on the first session ({startSchedule}) and stays up for the rest of the workshop.",
  'workshop.2026.preferences.phone_yes': "Yes, list my number (and let me see others')",
  'workshop.2026.preferences.phone_no': "No — I won't see other members' phone numbers",
  'workshop.2026.preferences.saved_number': 'Saved number: {phone}',
  'workshop.2026.preferences.update_number': 'Update my number',
  'workshop.2026.preferences.view_profile': 'View my profile',
  'workshop.2026.preferences.phone_label': 'Phone number',
  'workshop.2026.preferences.phone_updates_profile':
    'Updates your Recovery Commons profile for all meetings.',
  'workshop.2026.preferences.phone_saved_note':
    'Saved to your Recovery Commons profile and used when the list goes live.',
  'workshop.2026.preferences.phone_placeholder': 'e.g. (555) 123-4567',
  'workshop.2026.preferences.saving': 'Saving…',
  'workshop.2026.preferences.save_number': 'Save number',
  'workshop.2026.preferences.cancel': 'Cancel',
  'workshop.2026.preferences.phone_saved': 'Phone number saved.',
  'workshop.2026.preferences.email_heading': 'Workshop email list',
  'workshop.2026.preferences.email_help_with':
    'This shares your account email ({email}) with confirmed members. Same timing as the phone list.',
  'workshop.2026.preferences.email_help':
    'This shares your account email with confirmed members. Same timing as the phone list.',
  'workshop.2026.preferences.email_yes': "Yes, share my account email (and let me see others')",
  'workshop.2026.preferences.email_no': "No — I won't see other members' email addresses",
  'workshop.2026.preferences.shared_from_account': 'Shared from your account: {email}',
  'workshop.2026.preferences.email_login_used':
    'This is your login email, used when you opt in to the workshop email list.',
  'workshop.2026.preferences.sponsor_heading': 'Available to sponsor',
  'workshop.2026.preferences.sponsor_help':
    'Willing to be listed as an available sponsor on the workshop phone list?',
  'workshop.2026.preferences.yes': 'Yes',
  'workshop.2026.preferences.yes_temporary': 'Yes, temporary',
  'workshop.2026.preferences.no': 'No',
  'workshop.2026.preferences.saved': 'Preferences saved.',

  'workshop.2026.preferences.summary.name_label': 'Name on list',
  'workshop.2026.preferences.summary.name_now': 'Showing now',
  'workshop.2026.preferences.summary.name_at_start': 'When workshop starts',
  'workshop.2026.preferences.summary.name_never': "Don't ever show",
  'workshop.2026.preferences.summary.phone_label': 'Phone on list',
  'workshop.2026.preferences.summary.email_label': 'Email on list',
  'workshop.2026.preferences.summary.sponsor_label': 'Available to sponsor',
  'workshop.2026.preferences.summary.temporary': 'Temporary',
  'workshop.2026.preferences.summary.add_number': 'Yes — add your number',
  'workshop.2026.preferences.summary.no_contact': 'No contact info shared',
  'workshop.2026.preferences.summary.sharing_email': 'Sharing email',
  'workshop.2026.preferences.summary.sharing_phone': 'Sharing phone',
  'workshop.2026.preferences.summary.sharing_phone_add': 'Sharing phone (add number)',
  'workshop.2026.preferences.summary.sharing_both': 'Sharing phone and email',
  'workshop.2026.preferences.summary.name_now_frag': 'name on list now',
  'workshop.2026.preferences.summary.name_start_frag': 'name at start',
  'workshop.2026.preferences.summary.name_not_listed': 'name not listed',
  'workshop.2026.preferences.summary.open_sponsor': 'open to sponsor',
  'workshop.2026.preferences.summary.temp_sponsor': 'temp sponsor',

  // Phone list (member)
  'workshop.2026.phone_list.give_to_get':
    "Sharing goes both ways: you see members' phone numbers only if you list yours, and their emails only if you list yours.",
  'workshop.2026.phone_list.not_available':
    'The phone list is not available yet. The facilitator can enable it in workshop settings.',
  'workshop.2026.phone_list.need_verified':
    'You need to be a verified confirmed member to view the phone list.',
  'workshop.2026.phone_list.locked_title': 'Add your phone number to see the list',
  'workshop.2026.phone_list.locked_body':
    "Sharing goes both ways — list your number and you'll see everyone who has opted in.",
  'workshop.2026.phone_list.update_prefs': 'Update my preferences',
  'workshop.2026.phone_list.loading': 'Loading phone list…',
  'workshop.2026.phone_list.load_error': 'Could not load phone list',
  'workshop.2026.phone_list.empty': 'No other members have added their phone number yet.',
  'workshop.2026.phone_list.share_email_hint':
    "Share your email in preferences to see members' email addresses.",
  'workshop.2026.phone_list.col_name': 'Name',
  'workshop.2026.phone_list.col_phone': 'Phone',
  'workshop.2026.phone_list.col_email': 'Email',
  'workshop.2026.phone_list.col_sponsor': 'Sponsor?',
  'workshop.2026.phone_list.col_sponsor_print': 'Sponsor',
  'workshop.2026.phone_list.sponsor_yes': 'Yes',
  'workshop.2026.phone_list.sponsor_temp': 'Temp',
  'workshop.2026.phone_list.dash': '—',
  'workshop.2026.phone_list.share_email_to_see': 'Share your email to see',
  'workshop.2026.phone_list.print': 'Print phone list',
  'workshop.2026.phone_list.print_title': '{workshopName} — Phone list',
  'workshop.2026.phone_list.print_meta': 'Phone list · {date}',
  'workshop.2026.phone_list.print_privacy':
    'Private — for workshop members only. Please keep this information confidential and do not share outside the workshop.',
  'workshop.2026.phone_list.print_email_hidden':
    'Email addresses are hidden because you are not sharing yours. Add your email in workshop preferences to see them.',
  'workshop.2026.phone_list.member_fallback': 'Member',

  // Community / roster
  'workshop.2026.community.loading': 'Loading…',
  'workshop.2026.community.load_error': 'Could not load participant info',
  'workshop.2026.community.still_deciding': "Who's still deciding ({count})",
  'workshop.2026.community.lists_at_start':
    'Phone and email lists will be available to confirmed members when the workshop starts ({firstSession}). You can opt in under preferences.',
  'workshop.2026.community.lists_at_start_tbd':
    'Phone and email lists will be available to confirmed members when the workshop starts. You can opt in under preferences.',

  'workshop.2026.roster.confirmed_1': '(1 confirmed)',
  'workshop.2026.roster.confirmed_n': '({count} confirmed)',
  'workshop.2026.roster.deciding_1': '({total} with 1 still deciding)',
  'workshop.2026.roster.deciding_n': '({total} with {count} still deciding)',
  'workshop.2026.roster.community_title': 'Workshop community',
  'workshop.2026.roster.participants_title': 'Workshop Participants {headerCount}',
  'workshop.2026.roster.confirmed_short_1': '1 confirmed',
  'workshop.2026.roster.confirmed_short_n': '{count} confirmed',
  'workshop.2026.roster.interest_short_1': '1 on the interest list',
  'workshop.2026.roster.interest_short_n': '{count} on the interest list',
  'workshop.2026.roster.subtitle_combo': '{confirmed} · {interest}',
  'workshop.2026.roster.no_confirmed': 'No confirmed participants yet.',
  'workshop.2026.roster.joining_1': '1 person has confirmed and will be joining.',
  'workshop.2026.roster.joining_n': '{count} people have confirmed and will be joining.',
  'workshop.2026.roster.plus_1_other': '{names} + 1 other',
  'workshop.2026.roster.plus_n_others': '{names} + {count} others',
  'workshop.2026.roster.names_period': '{names}.',
  'workshop.2026.roster.interest_empty': 'No one is on the interest list right now.',
  'workshop.2026.roster.interest_1': "One person is on the interest list and hasn't confirmed yet.",
  'workshop.2026.roster.interest_n': "{count} people are on the interest list and haven't confirmed yet.",
  'workshop.2026.roster.viewer_confirmed_1': '1 person has confirmed.',
  'workshop.2026.roster.viewer_confirmed_n': '{count} people have confirmed.',
  'workshop.2026.roster.viewer_planning_1': "1 person has confirmed they're planning to attend.",
  'workshop.2026.roster.viewer_planning_n': "{count} people have confirmed they're planning to attend.",
  'workshop.2026.roster.viewer_interest_1': "1 person is on the interest list and hasn't confirmed yet{suffix}.",
  'workshop.2026.roster.viewer_interest_n': "{count} people are on the interest list and haven't confirmed yet{suffix}.",
  'workshop.2026.roster.including_you': ' (including you)',

  // First Step signup (member)
  'workshop.2026.first_step.title': 'First Step presentation sign-up',
  'workshop.2026.first_step.loading': 'Loading presentation sign-up…',
  'workshop.2026.first_step.not_open':
    'First Step presentation sign-up is not open yet. Your facilitator will enable it when the workshop begins Step One.',
  'workshop.2026.first_step.load_error': 'Could not load sign-up sheet',
  'workshop.2026.first_step.week_locked': 'That week is locked.',
  'workshop.2026.first_step.removed': 'You removed your sign-up.',
  'workshop.2026.first_step.remove_error': 'Could not remove sign-up',
  'workshop.2026.first_step.updated': 'Sign-up updated.',
  'workshop.2026.first_step.update_error': 'Could not update sign-up',
  'workshop.2026.first_step.archived':
    'This schedule is archived from when Step One sign-up was open.',
  'workshop.2026.first_step.instructions':
    'Sign up for one slot: present in this workshop, be an alternate, or mark that you presented outside (another meeting). Tap an open cell to sign up or move. Tap your name to remove your sign-up.',
  'workshop.2026.first_step.week': 'Week {n}',
  'workshop.2026.first_step.add_present': '+ Present',
  'workshop.2026.first_step.add_alt': '+ Alt',
  'workshop.2026.first_step.add_outside': '+ Outside',
  'workshop.2026.first_step.you_suffix': '{name} (you)',
  'workshop.2026.first_step.member': 'Member',
  'workshop.2026.first_step.summary_loading': 'Loading…',
  'workshop.2026.first_step.summary_not_open': 'Not open yet',
  'workshop.2026.first_step.summary_not_signed': 'Not signed up yet',
  'workshop.2026.first_step.summary_signed_dated':
    'Signed up as {role} for week {week} — {date}',
  'workshop.2026.first_step.summary_signed': 'Signed up as {role} for week {week}',
  'workshop.2026.first_step.role_presenter': 'presenter',
  'workshop.2026.first_step.role_alternate': 'alternate',
  'workshop.2026.first_step.role_outside': 'presented outside',
  'workshop.2026.first_step.status_alternate':
    'You are signed up as an alternate for week {week}.',
  'workshop.2026.first_step.status_outside':
    'You are signed up as presented outside the workshop for week {week}.',
  'workshop.2026.first_step.status_presenter':
    'You are signed up as a presenter for week {week}.',
  'workshop.2026.first_step.band_in': 'In workshop',
  'workshop.2026.first_step.band_alts': 'Alternates',
  'workshop.2026.first_step.band_outside': 'Outside workshop',
  'workshop.2026.first_step.col_presenter': 'Presenter',
  'workshop.2026.first_step.col_presenter_n': 'Presenter {n}',
  'workshop.2026.first_step.col_alternate': 'Alternate',
  'workshop.2026.first_step.col_alternate_n': 'Alternate {n}',
  'workshop.2026.first_step.col_outside': 'Outside',
  'workshop.2026.first_step.col_outside_full': 'Outside workshop',
  'workshop.2026.first_step.col_outside_n': 'Outside {n}',
  'workshop.2026.first_step.short_present': 'Present',
  'workshop.2026.first_step.short_alt': 'Alt',
  'workshop.2026.first_step.short_outside': 'Outside',

  // Discovery banner
  'workshop.2026.discovery.title': 'Working the steps again?',
  'workshop.2026.discovery.body':
    '— You have step work from before you joined this workshop. Use Save & Start over in the menu to archive it and begin fresh for the workshop. We keep a copy for you.',
  'workshop.2026.discovery.cta': 'Save & start over',
  'workshop.2026.discovery.dismiss': 'Dismiss',

  // Schedule templates (CMS stores English weekday; display via these keys)
  'workshop.2026.schedule.sunday': 'Sunday',
  'workshop.2026.schedule.sundays': 'Sundays',
  'workshop.2026.schedule.monday': 'Monday',
  'workshop.2026.schedule.mondays': 'Mondays',
  'workshop.2026.schedule.tuesday': 'Tuesday',
  'workshop.2026.schedule.tuesdays': 'Tuesdays',
  'workshop.2026.schedule.wednesday': 'Wednesday',
  'workshop.2026.schedule.wednesdays': 'Wednesdays',
  'workshop.2026.schedule.thursday': 'Thursday',
  'workshop.2026.schedule.thursdays': 'Thursdays',
  'workshop.2026.schedule.friday': 'Friday',
  'workshop.2026.schedule.fridays': 'Fridays',
  'workshop.2026.schedule.saturday': 'Saturday',
  'workshop.2026.schedule.saturdays': 'Saturdays',
  'workshop.2026.schedule.start_date_tbd': '{day}, {time} (start date TBD)',
  'workshop.2026.schedule.first_meeting_tbd': 'First meeting: {day}, {time} (date TBD)',
  'workshop.2026.schedule.first_meeting': 'First meeting: {date} · {time}',
  'workshop.2026.schedule.next_meeting': 'Next meeting: {date} · {time}',
  'workshop.2026.schedule.date_tbd': 'date TBD',
  'workshop.2026.schedule.day_time': '{day}, {time}',

  // FAQ section chrome + inline ask
  'workshop.2026.faq.heading': 'Frequently asked questions',
  'workshop.2026.faq.ask_signin': 'Sign in to send a question to the workshop facilitators.',
  'workshop.2026.faq.ask_signin_cta': 'Sign in to ask a question',
  'workshop.2026.faq.ask_thanks': 'Thank you — your question has been sent.',
  'workshop.2026.faq.ask_another': 'Ask another question',
  'workshop.2026.faq.ask_label': 'Your question',
  'workshop.2026.faq.ask_placeholder': 'What would you like to know?',
  'workshop.2026.faq.ask_error': 'Failed to send your question',
  'workshop.2026.faq.ask_sending': 'Sending…',
  'workshop.2026.faq.ask_send': 'Send question',
  'workshop.2026.faq.time_commitment_answer':
    'The workshop meets {schedule}, online. Beyond the meeting, you will spend time on step work between sessions — inventories, writing, and meetings with your sponsor. That varies by step and by person.',
  'workshop.2026.faq.past_workshops_answer':
    "Yes. This is our 12th annual step workshop. In the 11 workshops we've held since 2009, more than 200 people have worked all 12 Steps from start to finish.",

  'workshop.2026.faq.what-is-workshop.question': 'What is an SAA step workshop?',
  'workshop.2026.faq.what-is-workshop.answer':
    'A step workshop is a facilitated group that works all 12 steps of SAA together over many months. We meet at the same time each week online via Zoom. The group stays largely stable so we build trust, honesty, and momentum — working the steps in community rather than alone.',
  'workshop.2026.faq.who-is-it-for.question': 'Who is this workshop for?',
  'workshop.2026.faq.who-is-it-for.answer':
    'Anyone with a desire to stop addictive sexual behavior who is willing to work the 12 steps of SAA, can commit to weekly attendance (life happens; we understand), and is comfortable with online meetings — the same foundation as SAA meetings. No prior step work is required. People who have worked some or all of the steps before are also welcome — to complete the steps, work them again, and deepen recovery, or share wisdom.',
  'workshop.2026.faq.open-to-everyone.question': 'Is this workshop open to everyone?',
  'workshop.2026.faq.open-to-everyone.answer':
    "Yes. This workshop welcomes anyone seeking recovery from compulsive sexual acting out — regardless of gender, sexual orientation, gender identity, relationship status, faith, or background. SAA's only requirement for membership is a desire to stop addictive sexual behavior. You belong here.",
  'workshop.2026.faq.sponsor.question': 'Do I need a sponsor?',
  'workshop.2026.faq.sponsor.answer':
    'You will need a sponsor to complete Step One. If you do not have one yet, that is okay — part of preparing for the workshop is thinking about sponsorship, and the group can help you consider how to find one.',
  'workshop.2026.faq.how-long.question': 'How long does the workshop run?',
  'workshop.2026.faq.how-long.answer':
    'There is no fixed number of weeks. Workshops typically run 35–60 weeks, depending on group pace and consensus. We work one step at a time and move when the group is ready.',
  'workshop.2026.faq.cost.question': 'Is there any cost for the workshop?',
  'workshop.2026.faq.cost.answer':
    'No. There is no cost to join — the workshop and Recovery Commons are a labor of love, offered freely in the spirit of one addict helping another. There are no fees, dues, or charges to participate.',
  'workshop.2026.faq.weekly-session.question': 'What happens at each weekly session?',
  'workshop.2026.faq.weekly-session.answer':
    'Sessions focus on the current step with step-focused reading together, sharing, and encouragement. Between sessions, members learn and record their step work in Recovery Commons while working with their sponsors and others in the program.',
  'workshop.2026.faq.registration.question': 'How do I register?',
  'workshop.2026.faq.registration.answer':
    'Go to the Workshop tab above, create a free Recovery Commons account, and indicate your interest. When you are ready to commit, confirm you are planning to attend — then the Zoom link appears on the Workshop tab above before the first session. Missed the first meeting? You can still sign up and join us when you are ready.',
  'workshop.2026.faq.time-commitment.question': 'What is the weekly time commitment?',
  'workshop.2026.faq.past-workshops.question': 'Have you run workshops like this before?',
  'workshop.2026.faq.recovery-commons.question': 'What is Recovery Commons and how does it fit in?',
  'workshop.2026.faq.recovery-commons.answer':
    'As the facilitator of these workshops, I have seen how much easier people recover when they have a clear path through the steps, a place to record their work, and a simple way to share with their sponsor between weekly meetings. I built Recovery Commons to capture what we have learned over many years of workshop experience and to give members a structured, private home for their step work.\n\nRecovery Commons is a free online tool for your 12-step work — guided actions, inventories, and progress you own. Workshop members use it between sessions to stay on track and optionally share with a sponsor. The workshop itself (Zoom, calendar, phone list) lives on this page under the Workshop tab.',
  'workshop.2026.faq.privacy.question': 'Is my step work private?',
  'workshop.2026.faq.privacy.answer':
    'Yes. Your sensitive step work in Recovery Commons is encrypted; only you can read it. Workshop facilitators do not and cannot read your private inventories or step writing. Sharing with a sponsor or others is entirely under your control.',
  'workshop.2026.faq.ask.question': 'Have a question not answered here?',

  'workshop.2026.faq.past.2025_2026': 'Saturdays, Apr 2025 – May 2026 · 20 people · Online',
  'workshop.2026.faq.past.2024_2025': 'Saturdays, Mar 2024 – Mar 2025 · 20 · Online',
  'workshop.2026.faq.past.2023': 'Wednesdays, Jan 2023 – Oct 2023 · 15 · Online',
  'workshop.2026.faq.past.2022': 'Saturdays, Jan 2022 – Oct 2022 · 20 · Online',
  'workshop.2026.faq.past.2021': 'Saturdays, Feb 2021 – Oct 2021 · 20 · Online',
  'workshop.2026.faq.past.2019_2021': 'Saturdays, Sep 2019 – Mar 2021 · 20 · San Jose, CA + Online',
  'workshop.2026.faq.past.2018_2019': 'Thursdays, Jan 2018 – May 2019 · 20 · Indianapolis, IN',
  'workshop.2026.faq.past.2016_2017': 'Tuesdays, Jan 2016 – Mar 2017 · 15 · Fishers, IN',
  'workshop.2026.faq.past.2013_2015': 'Thursdays, Nov 2013 – Feb 2015 · 25 · Indianapolis, IN',
  'workshop.2026.faq.past.2011_2012': 'Mondays, Jan 2011 – Jun 2012 · 20 · Brownsburg, IN',
  'workshop.2026.faq.past.2009_2010': 'Thursdays, Jun 2009 – Aug 2010 · 30 · Indianapolis, IN',

  // Shared step page errors
  'step.page.error.load': 'Failed to load your progress. Please try refreshing the page.',
  'step.page.error.save_progress': 'Failed to save progress. Please try again.',
  'step.page.error.sign_in': 'Please sign in to save your progress.',
  'step.page.error.save_sponsor': 'Failed to save sponsor status.',

  // Auth error chrome (toasts + AuthErrorDisplay)
  'auth.error.title': 'Session Expired',
  'auth.error.session_expired': 'Your session has expired. Please log in again to continue.',
  'auth.error.session_expired_save':
    'Your session has expired. Please log in again to save your progress.',
  'auth.error.session_invalid': 'Your session is no longer valid. Please log in again.',
  'auth.error.please_log_in': 'Please log in to continue.',
  'auth.error.no_token': 'No authentication token found. Please log in.',
  'auth.error.not_logged_in': 'User not logged in. Please log in.',
  'auth.error.generic': 'An error occurred. Please try again.',
  'auth.error.log_in_again': 'Log In Again',
  'auth.error.help_after_login': 'After logging in, your work will be saved automatically.',

  // Step help panel chrome (not long-form help body)
  'step.help.close_aria': 'Close help',
  'step.help.button_title': 'Help',
  'step.help.link_default': 'How step work in Recovery Commons works',
  'step.help.panel_title_app': 'Help: Recovery Commons',
  'step.help.panel_title_workshop': 'Help: Workshop',

  // Working Steps hub (/saa/12-steps)
  'working_steps.page.title': "Working SAA's 12 Steps",
  'working_steps.page.description':
    'The 12 Steps of Sex Addicts Anonymous provide a proven path to recovery from sexual addiction. Each step builds upon the previous ones, creating a comprehensive program for spiritual and emotional healing.',
  'working_steps.loading': 'Loading your progress...',
  'working_steps.loading_sub': 'Please wait while we retrieve your step data',
  'working_steps.login.title': 'Track Your Progress',
  'working_steps.login.body':
    'Login or create an account to track your progress through the 12 steps and receive personalized guidance and support in your recovery journey.',
  'working_steps.login.login': 'Login',
  'working_steps.login.create': 'Create Account',
  'working_steps.desc.1':
    'The first step involves honest self-assessment and admitting powerlessness over sexual addiction.',
  'working_steps.desc.2':
    'Developing faith in a higher power that can help restore mental and emotional health.',
  'working_steps.desc.3':
    'Surrendering control and trusting in a higher power for guidance and strength.',
  'working_steps.desc.4':
    'A thorough self-examination of resentments, fears, and harms done to others.',
  'working_steps.desc.5':
    'Sharing the inventory with a trusted person and experiencing the relief of honesty.',
  'working_steps.desc.6':
    'Becoming willing to let go of character defects and negative patterns.',
  'working_steps.desc.7':
    'Asking for help in removing character defects and developing humility.',
  'working_steps.desc.8':
    'Identifying those harmed by our actions and preparing to make amends.',
  'working_steps.desc.9':
    'Taking action to repair relationships and make amends where appropriate.',
  'working_steps.desc.10':
    'Ongoing self-examination and immediate admission of mistakes.',
  'working_steps.desc.11':
    'Developing spiritual practices and seeking guidance through prayer and meditation.',
  'working_steps.desc.12':
    'Sharing the message of recovery and living by spiritual principles in all areas of life.',

  'working_steps.status.not_started': 'Not Started',
  'working_steps.status.in_progress': 'In Progress',
  'working_steps.status.completed': 'Completed',
  'working_steps.card.step_label': 'Step {n}',
  'working_steps.card.step_fallback': 'Step',
  'working_steps.card.sign_in_prompt': 'Sign in to track your progress',
  'working_steps.card.sign_in': 'Sign in',

  'working_steps.journey.loading': 'Loading journey data...',
  'working_steps.journey.load_failed': 'Failed to load journey data',
  'working_steps.journey.all_done':
    'Wow! Congratulations for working through all 12 steps of SAA!',
  'working_steps.journey.start_gs':
    'You can do this! Start with Getting Started below, then begin Step One.',
  'working_steps.journey.gs_in_progress':
    'Keep going with Getting Started — then begin Step One.',
  'working_steps.journey.gs_start': 'Start with Getting Started before Step One.',
  'working_steps.journey.gs_done':
    'Great work on Getting Started! Click on Step 1 below to begin working the steps.',
  'working_steps.journey.step12_partial':
    "Congratulations on completing Step 12! You've made great progress. To complete all 12 steps, continue working on the steps you haven't finished yet.",
  'working_steps.journey.step_done':
    'Congratulations on completing Step {n}! Keep going. Click on Step {next} below to continue your progress',
  'working_steps.journey.step_in_progress':
    'You are making great progress - keep going! Click on Step {n} to continue your efforts',
  'working_steps.journey.started': 'You have started working the 12 steps. Keep going!',

  'working_steps.tab.label': 'Work my steps',
  'working_steps.tab.title': 'Your SAA 12 Step checklist',
  'working_steps.tab.login_title': 'Sign in to track your 12 step work',
  'working_steps.tab.login_body':
    'Create an account or sign in to track your progress through the 12 steps and receive personalized guidance in your recovery journey.',
  'working_steps.tab.login_cta': 'Sign in / Create account',
  'working_steps.gs.label': 'Getting Started',
  'working_steps.gs.title':
    'Introduction to SAA — addiction, fellowship, sponsor, and your 3 Circles',
  'working_steps.gs.description':
    'Readings, video, and personal tools before you begin Step One.',

  // Step One page — deep workspace chrome
  'step.1.page.nudge_lead': 'We recommend completing',
  'step.1.page.nudge_tail': 'before working Step One.',
  'step.1.page.nudge_dismiss_aria': 'Dismiss',
  'step.1.page.error.save_presentation': 'Failed to save presentation notes.',
  'step.1.page.error.save_writing': 'Failed to save writing progress.',

  // Step One — presenting my First Step (action 5 fields + presentation plan)
  'step.1.presentation.date_label': 'Date presented',
  'step.1.presentation.place_label': 'Place',
  'step.1.presentation.place_placeholder': 'e.g. Tuesday night meeting',
  'step.1.presentation.reflection_label': 'How it went',
  'step.1.presentation.reflection_placeholder': 'How you felt after presenting',
  'step.1.presentation.target_date_label': 'Target date (optional)',
  'step.1.presentation.venue_label': 'Where you plan to share',
  'step.1.presentation.venue_select': '— Select —',
  'step.1.presentation.venue_note_label': 'Venue note',

  // Step One — writing method names (progress lines, banners)
  'step.1.mode.not_chosen': 'Not chosen yet',
  'step.1.mode.external': 'Written and stored elsewhere',
  'step.1.mode.upload': 'Uploaded document',
  'step.1.mode.typed': 'Type it here',
  'step.1.mode.wizard': 'Guided walkthrough',
  'step.1.mode.fallback': 'First Step writing',

  'step.1.venue.workshop': 'Workshop',
  'step.1.venue.home_meeting': 'Home meeting',
  'step.1.venue.sponsor_only': 'With sponsor only',
  'step.1.venue.other': 'Other',

  // Step One — action 3 progress line
  'step.1.progress.in_progress': 'in progress',
  'step.1.progress.started': 'started',
  'step.1.progress.last_worked': 'last worked {date}',

  // Step One — story length labels
  'step.1.length.word_one': '{count} word',
  'step.1.length.word_many': '{count} words',
  'step.1.length.aloud_one': '~{min} minute long when read aloud',
  'step.1.length.aloud_many': '~{min} minutes long when read aloud',
  'step.1.length.aloud_range': '~{min}–{max} minutes long when read aloud',
  'step.1.length.summary': 'Length: {words} · {aloud}',

  // Step One — method chooser cards
  'step.1.method.wizard.title': 'Guided walkthrough',
  'step.1.method.wizard.description': 'Step-by-step prompts, then assemble your words into a draft.',
  'step.1.method.typed.title': 'Type it here',
  'step.1.method.typed.description': 'Plain-text editor with auto-save in Recovery Commons.',
  'step.1.method.upload.title': 'Upload a document',
  'step.1.method.upload.description':
    'Supported formats: .txt, .docx, .pdf, .md. Max size: 5 MB. Original file stored encrypted.',
  'step.1.method.external.title': 'Written and stored elsewhere',
  'step.1.method.external.description':
    'Pen and paper, another app, or already finished — track progress here.',
  'step.1.method.fallback_title': 'First Step writing',

  // Step One — numbered method steps inside the workspace
  'step.1.method_step.walkthrough.title': 'Your story',
  'step.1.method_step.walkthrough.body':
    "Answer each prompt below as best you can — then click Next. Click Skip for prompts you don't have an answer for, aren't ready for, or already covered in a previous answer. These prompts will take you through all the parts of a typical First Step story. Take a break at any time — your entries are saved and you can start up where you left off next time.",
  'step.1.method_step.assemble.title': 'Assemble your draft',
  'step.1.method_step.assemble.body':
    'Combine your walkthrough answers into one draft — your own words, in order.',
  'step.1.method_step.presentation.title': 'Presentation plan',
  'step.1.method_step.presentation.body':
    'Set when and where you plan to share to help you and your sponsor prepare.',
  'step.1.method_step.review.title': 'Review & share',
  'step.1.method_step.review.body':
    'Stay in contact with your sponsor and share your First Step story to be reviewed when you need help and before you finally present it.',
  'step.1.method_step.your-story.title': 'Your story',
  'step.1.method_step.your-story.body':
    'Type your story below (Insert template if an outline would help).',
  'step.1.method_step.draft-checklist.title': 'Draft checklist',
  'step.1.method_step.draft-checklist.body':
    'Optional reminders about common First Step topics — your story is never changed for you.',
  'step.1.method_step.upload.title': 'Upload your document',
  'step.1.method_step.upload.body':
    'Upload your document (.txt, .docx, .pdf, or .md — max 5 MB).',
  'step.1.method_step.review-edit.title': 'Review & edit',
  'step.1.method_step.review-edit.body': 'Review and edit the extracted text below if needed.',
  'step.1.method_step.where-kept.title': 'Where you keep it',
  'step.1.method_step.where-kept.body':
    'Note where you keep your story (optional) — on paper, another app, with your sponsor, etc.',

  // Step One — story sections (chapters) and coverage topics
  'step.1.chapters.ch1': 'Introduction',
  'step.1.chapters.ch2': 'Family & early life',
  'step.1.chapters.ch3': 'How addiction developed',
  'step.1.chapters.ch4': 'Powerlessness',
  'step.1.chapters.ch5': 'Unmanageability',
  'step.1.chapters.ch6': 'Hitting bottom & recovery',
  'step.1.chapters.ch7': 'Closing',

  'step.1.topics.introduction': 'Introduction (name, fellowship)',
  'step.1.topics.family_origin': 'Family of origin',
  'step.1.topics.childhood_sexuality': 'Childhood sexuality / early experiences',
  'step.1.topics.addiction_development': 'How addiction developed',
  'step.1.topics.acting_out_patterns': 'Acting-out patterns',
  'step.1.topics.powerlessness': 'Powerlessness (could not stop)',
  'step.1.topics.failed_stops': 'Failed attempts to stop',
  'step.1.topics.unmanageability': 'Unmanageability',
  'step.1.topics.relationships_impact': 'Impact on relationships',
  'step.1.topics.consequences': 'Consequences (work, legal, health)',
  'step.1.topics.bottom': 'Bottom / turning point',
  'step.1.topics.recovery_start': 'How recovery began',
  'step.1.topics.closing': 'Closing (name, fellowship)',

  // Step One — guided walkthrough prompts
  'step.1.prompts.intro_name.text':
    'How will you introduce yourself at the start of your First Step?',
  'step.1.prompts.intro_name.placeholder': 'My name is… and I am a sex addict.',
  'step.1.prompts.family_overview.text': 'Describe your early years growing up',
  'step.1.prompts.family_overview.nudge':
    'What was your family like, how was your relationship with your parents and any siblings, did you have friends, did you move a lot, etc?',
  'step.1.prompts.family_overview.placeholder':
    'I grew up in a family where… (home life, parents, siblings, friends, moves)',
  'step.1.prompts.family_sex_attitudes.text':
    'What did you learn about sex, shame, and secrecy growing up?',
  'step.1.prompts.family_sex_attitudes.nudge':
    "When and how were you introduced to sex, what did you feel about sex, did anyone explain the 'birds and the bees' to you, were you abused in any way that impacted your view of sex, what other strong memories about sex do you have from growing up, etc.",
  'step.1.prompts.family_sex_attitudes.placeholder':
    'Growing up, I learned that sex was… / Messages about sex and secrecy were…',
  'step.1.prompts.addiction_start.text':
    'When did you first notice compulsive sexual behavior? What was happening in your life then?',
  'step.1.prompts.addiction_start.placeholder':
    'I first noticed my sexual behavior felt compulsive when… (age, life situation)',
  'step.1.prompts.progression.text':
    'How did your acting-out escalate over time? What behaviors increased in frequency or intensity?',
  'step.1.prompts.progression.placeholder':
    'Over time, my acting out escalated from… to… (frequency, intensity, new behaviors)',
  'step.1.prompts.acting_out_examples.text':
    'What are or were your main acting-out behavior(s)? For each, share the lead-up, what happened in plain language, and how you felt before and after.',
  'step.1.prompts.acting_out_examples.nudge':
    'No graphic sexual detail. Don’t name specific people, places, websites, or apps.',
  'step.1.prompts.acting_out_examples.placeholder':
    'If you have more than one type of acting out, describe each separately.',
  'step.1.prompts.powerlessness_moment.text':
    'Describe times you tried to stop or control your behavior and could not — what happened and how did you feel?',
  'step.1.prompts.powerlessness_moment.nudge':
    'Powerlessness means you could not stop on your own, even when you wanted to.',
  'step.1.prompts.powerlessness_moment.placeholder':
    "One time I tried to stop or control my behavior by… but I couldn't because…",
  'step.1.prompts.failed_stops.text':
    'What promises did you make to yourself or others about stopping? What happened when you tried?',
  'step.1.prompts.failed_stops.placeholder':
    'I promised myself (or someone else) I would stop when… Instead, what happened was…',
  'step.1.prompts.unmanageability_overview.text':
    'In what areas of life did your addiction create chaos or damage?',
  'step.1.prompts.unmanageability_overview.nudge':
    'Think about relationships, work, finances, health, spirituality, self-respect, hope, etc.',
  'step.1.prompts.unmanageability_overview.placeholder':
    'My addiction created chaos in… (e.g. relationships, work, money, health, self-respect, hope)',
  'step.1.prompts.relationships.text': 'How did your addiction affect your closest relationships?',
  'step.1.prompts.relationships.placeholder':
    'My closest relationships were affected when I… (lying, distance, broken trust, etc.)',
  'step.1.prompts.consequences.text':
    'What concrete consequences did you face — lost relationships, divorces, lost jobs, legal issues, health problems, or other costs?',
  'step.1.prompts.consequences.placeholder':
    'Concrete costs I faced included… (e.g. lost relationship, job, legal trouble, health)',
  'step.1.prompts.recent_episode.text':
    'Describe your last significant acting out episode, how it made you feel, and any consequences',
  'step.1.prompts.recent_episode.placeholder':
    'My last significant acting-out episode: what led up to it, what happened in plain language, how I felt after…',
  'step.1.prompts.feelings.text':
    'What feelings did you have about yourself and your life at your worst point?',
  'step.1.prompts.feelings.placeholder':
    'At my worst, I felt… about myself, my life, and the people around me.',
  'step.1.prompts.bottom.text':
    'What was your bottom — the moment or period when you knew you could not go on this way?',
  'step.1.prompts.bottom.placeholder':
    'My bottom was when I realized I could not go on this way — e.g. a crisis, or a moment of despair…',
  'step.1.prompts.recovery_start.text': 'How did you find SAA or begin working recovery?',
  'step.1.prompts.recovery_start.placeholder':
    'I found SAA / began working recovery when… (how you got here, first meeting, sponsor, etc.)',
  'step.1.prompts.closing.text': 'How will you close your First Step?',
  'step.1.prompts.closing.placeholder':
    'My name is… and I am a sex addict — and that is my story, so far.',

  // Step One — draft checklist reminders
  'step.1.checklist.introduction.feedback':
    'Many First Steps start with your first name and saying you are a sex addict.',
  'step.1.checklist.introduction.recommendation':
    'Add that at the start if it fits how you want to share.',
  'step.1.checklist.powerlessness.feedback':
    "Include a time you tried to stop or control your behavior and couldn't.",
  'step.1.checklist.powerlessness.recommendation':
    "One clear example is enough — that's the heart of Step One.",
  'step.1.checklist.unmanageability.feedback':
    'Say how the addiction hurt parts of your life — relationships, work, money, health, or self-respect.',
  'step.1.checklist.unmanageability.recommendation': 'One or two examples are enough.',
  'step.1.checklist.length.feedback':
    "Your draft is short ({count} words) — that's OK while you're getting started.",
  'step.1.checklist.length.recommendation': 'Add more detail wherever you feel ready.',
  'step.1.checklist.length_long.feedback': 'Your draft is about {count} words — {aloud}.',
  'step.1.checklist.length_long.recommendation':
    'You can shorten later if you want; nothing is changed unless you edit yourself.',

  // Step One — writing workspace chrome
  'step.1.writing.close': 'Close',
  'step.1.writing.cancel': 'Cancel',
  'step.1.writing.continue': 'Continue',
  'step.1.writing.save': 'Save',
  'step.1.writing.dismiss': 'Dismiss',
  'step.1.writing.loading': 'Loading your saved work…',
  'step.1.writing.try_again': 'Try again',
  'step.1.writing.starting': 'Starting…',
  'step.1.writing.start_new_draft': 'Start a new draft',
  'step.1.writing.view_earlier_saves': 'View earlier saves',
  'step.1.writing.encryption_reminder':
    'Reminder: Story text and uploads are encrypted in Recovery Commons. Progress shares never include your story text.',

  'step.1.writing.error.decrypt':
    'We could not unlock this First Step copy — it was saved with an old encryption key. Other people’s work is not affected. You can start a new draft for the demo; that only replaces this unreadable copy on your account.',
  'step.1.writing.error.auth': '{message} Then open the writing workspace again.',
  'step.1.writing.error.load_with_local':
    'We could not load your story from Recovery Commons. You may still have a save on this phone or computer.',
  'step.1.writing.error.load_generic':
    'We could not load your saved work. Nothing was deleted on the server — try again or refresh the page. If you just switched accounts, sign out, refresh, and sign back in.',
  'step.1.writing.error.reload_failed':
    'Could not reload your saved work. Try refreshing the page.',
  'step.1.writing.error.start_fresh_failed': 'Could not start a new draft. Please try again.',

  'step.1.writing.protected_title': 'Your work was protected',
  'step.1.writing.protected_message':
    'A save was stopped because it would have erased your story. Your last good copy is still safe.',
  'step.1.writing.protected_ok': 'OK',
  'step.1.writing.save_failed_title': 'Save failed',
  'step.1.writing.save_verify_failed':
    'Could not verify your saved work before saving. Please try again.',

  'step.1.writing.restore_success':
    'Restored an earlier save. Your previous text was kept as another save.',
  'step.1.writing.restore_confirm_title': 'Use this earlier save?',
  'step.1.writing.restore_confirm_archive':
    'Restore your story from {label}? Your current text is kept as another save.',
  'step.1.writing.restore_confirm_local':
    'Restore the copy saved on this phone or computer at {savedAt}? Your current text is kept as another save.',
  'step.1.writing.restore_confirm_ok': 'Use this save',
  'step.1.writing.restore_failed': 'Could not restore that save. Try again.',
  'step.1.writing.no_local_save': 'No save found on this phone or computer.',

  'step.1.writing.method_label': 'Method:',
  'step.1.writing.change_method': 'Change method',
  'step.1.writing.change_method_title': 'Change writing method?',
  'step.1.writing.change_method_message':
    'Your walkthrough answers and draft are kept. Your current work is saved first. Continue?',
  'step.1.writing.return_chooser_title': 'Change method?',
  'step.1.writing.return_chooser_message':
    'Your work is kept. Your current work is saved first. Continue to the method chooser?',

  'step.1.writing.path_picker_title': 'How would you like to write your First Step story?',
  'step.1.writing.path_picker_intro':
    'Choose one approach to get started. You can change later — your saved work is kept in Recovery Commons.',

  'step.1.writing.external_note_label': 'Where I keep it (optional)',
  'step.1.writing.external_note_placeholder':
    'e.g. journal at home, Google Doc, sponsor has a copy',

  'step.1.writing.insert_template': 'Insert template',
  'step.1.writing.insert_template_title': 'Insert template?',
  'step.1.writing.insert_template_message':
    'This replaces your current story text with the outline template. Your current work is saved first.',
  'step.1.writing.story_aria': 'First Step story',
  'step.1.writing.story_placeholder': 'Write your First Step story here…',
  'step.1.writing.section_your_story': 'Your story',

  'step.1.writing.upload_choose': 'Choose a file to upload',
  'step.1.writing.choose_file': 'Choose File',
  'step.1.writing.no_file_chosen': 'No file chosen',
  'step.1.writing.upload_file_label': 'File:',
  'step.1.writing.download_original': 'Download original',
  'step.1.writing.upload_failed': 'Upload failed',
  'step.1.writing.upload_replace_title': 'Replace with uploaded file?',
  'step.1.writing.upload_replace_message':
    'This replaces your current story text with the uploaded document. Your current work is saved first.',
  'step.1.writing.upload_replace_ok': 'Upload and replace',
  'step.1.writing.extracted_aria': 'Extracted story text',
  'step.1.writing.section_extracted': 'Extracted story',

  'step.1.writing.jump_to_section': 'Jump to section:',
  'step.1.writing.chapter_optional_suffix': ' (optional)',
  'step.1.writing.prompt_label': 'Prompt',
  'step.1.writing.prompt_of': 'of',
  'step.1.writing.previous': 'Previous',
  'step.1.writing.next': 'Next',
  'step.1.writing.skip': 'Skip',

  'step.1.writing.assembling': 'Assembling…',
  'step.1.writing.assemble_cta': 'Assemble my words',
  'step.1.writing.assemble_stale_note':
    "You've changed one or more walkthrough answers since this draft was assembled.",
  'step.1.writing.updating_draft': 'Updating draft…',
  'step.1.writing.update_draft_cta': 'Update draft from my answers',
  'step.1.writing.draft_current_note':
    'Your assembled draft ({words}) matches your current walkthrough answers.',
  'step.1.writing.no_answers_yet': 'Add some walkthrough answers first.',
  'step.1.writing.update_draft_title': 'Update assembled draft?',
  'step.1.writing.update_draft_message':
    'This replaces your assembled draft with a fresh version from your walkthrough answers. Manual edits in the draft will be lost. Your current work is saved first.',
  'step.1.writing.update_draft_ok': 'Update draft',
  'step.1.writing.assemble_title': 'Assemble draft?',
  'step.1.writing.assemble_message':
    'This builds a new draft from your walkthrough answers and replaces your current story text. Your current work is saved first.',
  'step.1.writing.assemble_ok': 'Assemble',
  'step.1.writing.assembled_long':
    'Your answers were assembled into a draft. Open the draft editor when you are ready to review.',
  'step.1.writing.assembled_short': 'Your answers were assembled into a draft below.',
  'step.1.writing.assembled_aria': 'Assembled First Step draft',
  'step.1.writing.section_assembled_current': 'Your assembled draft',
  'step.1.writing.section_assembled': 'Assembled draft',

  'step.1.writing.draft_ready_lead': 'Your draft is ready —',
  'step.1.writing.draft_ready_tail': '. Use the draft editor to read or make changes.',
  'step.1.writing.open_draft_editor': 'Open draft editor',
  'step.1.writing.draft_editor_title': 'My First Step draft',
  'step.1.writing.draft_editor_close_aria': 'Close draft editor',
  'step.1.writing.unsaved_changes': 'Unsaved changes',
  'step.1.writing.discard_title': 'Discard changes?',
  'step.1.writing.discard_message':
    'You have unsaved edits to your draft. Discard them and close the editor?',
  'step.1.writing.discard_ok': 'Discard',
  'step.1.writing.keep_editing': 'Keep editing',
  'step.1.writing.save_empty_title': 'Save empty draft?',
  'step.1.writing.save_empty_message':
    'This will clear your assembled story text. Your walkthrough answers are kept unless you change them separately. Your current work is saved first.',
  'step.1.writing.save_empty_ok': 'Save empty draft',

  'step.1.writing.checklist_hint':
    'Optional reminders — pick what helps, ignore the rest. Your story is never changed for you. These are simple reminders, not an automated review of your writing.',
  'step.1.writing.checklist_aria': 'Draft checklist',
  'step.1.writing.checklist_consider': 'Consider:',

  'step.1.writing.view': 'View',
  'step.1.writing.download': 'Download',
  'step.1.writing.print': 'Print',
  'step.1.writing.share_story': 'Share First Step story',
  'step.1.writing.share_confirm_message':
    'This creates a link with your full story text. It expires in 3 days. Only share with people you trust (e.g. your sponsor).',
  'step.1.writing.share_empty': 'Add story text before sharing.',
  'step.1.writing.my_first_step': 'My First Step',

  'step.1.writing.download_title': 'Download My First Step',
  'step.1.writing.download_intro': 'Choose a format to save on your device.',
  'step.1.writing.download_pdf': 'PDF (.pdf)',
  'step.1.writing.download_docx': 'Word document (.docx)',
  'step.1.writing.download_txt': 'Plain text (.txt)',
  'step.1.writing.download_failed': 'Download failed. Try again.',

  'step.1.writing.clear_title': 'Clear all First Step content',
  'step.1.writing.clear_body_1':
    'This permanently removes your First Step story, walkthrough answers, uploaded document, and presentation plan from Recovery Commons.',
  'step.1.writing.clear_body_2':
    'Save a copy somewhere you control before continuing — for example on your computer, in email, or with your sponsor.',
  'step.1.writing.clear_ack':
    "I have saved a copy outside Recovery Commons (or I don't need one).",
  'step.1.writing.clearing': 'Clearing…',
  'step.1.writing.clear_footer_link': 'Clear First Step content (requires confirmation)',
  'step.1.writing.clear_success': 'Your First Step content was cleared.',
  'step.1.writing.clear_failed': 'Could not clear your content. Try again.',

  'step.1.writing.missing_story':
    'Your story looks empty here, but we found earlier saves. Would you like to look at them?',
  'step.1.writing.saving': 'Saving…',
  'step.1.writing.saved': 'Saved',
  'step.1.writing.save_error': 'Save failed — retry by editing',
  'step.1.writing.something_missing': 'Something missing? View earlier saves',

  'step.1.writing.template': `My name is ______, and I am a sex addict. This is the story of my addiction, my helplessness in controlling it on my own, and the unmanageability it has caused in my life.

[Family of origin and early experiences — how upbringing related to shame, validation, or secrecy around sex]

[How my addiction developed — progression over time]

[Examples of powerlessness — times I tried to stop and could not]

[Examples of unmanageability — impact on relationships, work, finances, health, spirit]

[Optional: my bottom and how I found recovery]

My name is ______, and I am a sex addict — and that is my story, so far.`,

  // Step One — earlier saves (recover overlay)
  'step.1.recover.title': 'Earlier saves of your story',
  'step.1.recover.intro':
    'Recovery Commons keeps earlier saves while you work. If you restore one, your current story is kept as another save — nothing is thrown away.',
  'step.1.recover.local_heading': 'Saved on this phone or computer',
  'step.1.recover.use_local': 'Use save from {date}',
  'step.1.recover.server_heading': 'Earlier saves in Recovery Commons',
  'step.1.recover.loading': 'Loading earlier saves…',
  'step.1.recover.empty':
    'No earlier saves are available yet. They are created automatically as you work.',
  'step.1.recover.use_this': 'Use this save',

  // ===== BEGIN step.4.inventory.* — Step Four inventory deep workspace =====
  // Modal chrome
  'step.4.inventory.modal_title': 'Step Four Inventories',
  'step.4.inventory.close': 'Close',
  'step.4.inventory.header_title': '{type} Inventory',
  'step.4.inventory.completed_checkbox': 'Inventory Completed',
  'step.4.inventory.completed_checkbox_for_type': "I've completed my {type} inventory",
  'step.4.inventory.mobile.progress': '{done} of {total} inventories marked complete',
  'step.4.inventory.mobile.complete_when_empty': 'Add at least one item to this inventory first.',
  'step.4.inventory.add_to_list': 'Add to List',
  'step.4.inventory.counts': '({total} total, {complete} completed)',
  'step.4.inventory.saving': 'Saving...',
  'step.4.inventory.saved': 'Saved',
  'step.4.inventory.save_failed_badge': 'Save failed',
  'step.4.inventory.click_for_details': 'click to see/add details',
  'step.4.inventory.delete': 'Delete',
  'step.4.inventory.help_inventories_title': 'Get help with inventories',
  'step.4.inventory.help_field_title': 'Get help with this field',
  'step.4.inventory.help_dialog_title': 'Help',
  'step.4.inventory.help_unavailable': '<h3>Help</h3><p>Help content not available.</p>',
  'step.4.inventory.help_unavailable_field': 'Help content not available for this field.',

  // Inventory types
  'step.4.inventory.type.resentments': 'Resentments',
  'step.4.inventory.type.fears': 'Fears',
  'step.4.inventory.type.harms': 'Harms Done',
  'step.4.inventory.my.resentments': 'My Resentments',
  'step.4.inventory.my.fears': 'My Fears',
  'step.4.inventory.my.harms': 'My Harms Done',

  // Status row / tab status
  'step.4.inventory.status.not_started': 'not started',
  'step.4.inventory.status.completed': 'completed ({count})',
  'step.4.inventory.status.in_progress': 'in progress ({count})',

  // Quick add
  'step.4.inventory.entry.resentments.label': 'Who do I resent?',
  'step.4.inventory.entry.resentments.placeholder':
    'e.g., My sister Julie, My boss, The government...',
  'step.4.inventory.entry.resentments.welcome': 'Start by adding your first resentment above',
  'step.4.inventory.entry.fears.label': 'What do I fear?',
  'step.4.inventory.entry.fears.placeholder': 'e.g., Fear of rejection, Fear of failure...',
  'step.4.inventory.entry.fears.welcome': 'Start by adding your first fear above',
  'step.4.inventory.entry.harms.label': 'Who did I harm?',
  'step.4.inventory.entry.harms.placeholder': 'e.g., My wife, My children, My friend John...',
  'step.4.inventory.entry.harms.welcome': 'Start by adding your first harm above',

  // Impact checkboxes
  'step.4.inventory.impact.self_esteem': 'Self-esteem',
  'step.4.inventory.impact.pride': 'Pride',
  'step.4.inventory.impact.pocketbook': 'Pocketbook',
  'step.4.inventory.impact.personal_relationships': 'Personal Relationships',
  'step.4.inventory.impact.sexual_relationships': 'Sexual Relationships',
  'step.4.inventory.impact.emotional_security': 'Emotional Security',
  'step.4.inventory.impact.ambitions': 'Ambitions',

  // Character defect checkboxes
  'step.4.inventory.defect.controlling': 'Controlling',
  'step.4.inventory.defect.dishonest': 'Dishonest',
  'step.4.inventory.defect.disengaged': 'Disengaged',
  'step.4.inventory.defect.entitled': 'Entitled',
  'step.4.inventory.defect.frightened': 'Frightened',
  'step.4.inventory.defect.greedy': 'Greedy',
  'step.4.inventory.defect.inconsiderate': 'Inconsiderate',
  'step.4.inventory.defect.impulsive': 'Impulsive',
  'step.4.inventory.defect.lustful': 'Lustful',
  'step.4.inventory.defect.self_pity': 'Self-pity',
  'step.4.inventory.defect.selfish': 'Selfish',
  'step.4.inventory.defect.willful': 'Willful',

  // Entry detail fields — resentments
  'step.4.inventory.field.who.label': 'Who do I resent?',
  'step.4.inventory.field.who.placeholder': 'Who',
  'step.4.inventory.field.why.label': 'Why do I resent them?',
  'step.4.inventory.field.why.placeholder': 'What happened? What did they do?',
  'step.4.inventory.field.feeling.label': 'How did that make me feel?',
  'step.4.inventory.field.feeling.placeholder': 'e.g., angry, hurt, betrayed, embarrassed...',
  'step.4.inventory.field.impact.label': 'What was lost, taken, or threatened inside me?',
  'step.4.inventory.field.my_part.label': 'What was my part in this resentment?',
  'step.4.inventory.field.my_part.placeholder':
    "Be honest about your role, even if it's small...",

  // Entry detail fields — fears
  'step.4.inventory.field.fear.label': 'What do I fear?',
  'step.4.inventory.field.fear.placeholder': 'Fear',
  'step.4.inventory.field.fear_why.label': 'Why do I have this fear?',
  'step.4.inventory.field.fear_why.placeholder': "What's behind this fear? What caused it?",
  'step.4.inventory.field.fear_impact.label': 'How does this fear affect me?',

  // Entry detail fields — harms
  'step.4.inventory.field.who_harmed.label': 'Who did I harm?',
  'step.4.inventory.field.who_harmed.placeholder': 'Who Harmed',
  'step.4.inventory.field.how.label': 'How did I harm them?',
  'step.4.inventory.field.how.placeholder': 'What did you do that hurt them?',
  'step.4.inventory.field.defects.label': 'What part of my character caused this harm?',
  'step.4.inventory.field.alternative.label': 'What could I have done differently?',
  'step.4.inventory.field.alternative.placeholder': 'What would have been a better choice?',

  // Dialogs
  'step.4.inventory.dialog.ok': 'OK',
  'step.4.inventory.dialog.this_entry': 'this entry',
  'step.4.inventory.dialog.session_expired_title': 'Session Expired',
  'step.4.inventory.dialog.session_expired_changes':
    'Your session has expired. Please log out and log back in to continue saving changes.',
  'step.4.inventory.dialog.session_expired_entries':
    'Your session has expired. Please log out and log back in to continue saving entries.',
  'step.4.inventory.dialog.not_logged_in_title': 'Not Logged In',
  'step.4.inventory.dialog.not_logged_in_message': 'Please log in to save inventory entries.',
  'step.4.inventory.dialog.encryption_error_title': 'Encryption Error',
  'step.4.inventory.dialog.encryption_error_message':
    'Unable to encrypt your data. Please try refreshing the page. If the problem persists, try logging out and back in.',
  'step.4.inventory.dialog.network_error_title': 'Network Error',
  'step.4.inventory.dialog.network_error_message':
    'Unable to save. Please check your internet connection and try again.',
  'step.4.inventory.dialog.save_failed_title': 'Save Failed',
  'step.4.inventory.dialog.save_failed_message':
    'Unable to save this entry. Please try again. If the problem persists, try refreshing the page.',
  'step.4.inventory.dialog.close_unsaved':
    'Failed to save your changes. If you close now, you will lose your recent work. Do you want to try again or close anyway?',
  'step.4.inventory.dialog.close_anyway': 'Close Anyway',
  'step.4.inventory.dialog.try_again': 'Try Again',
  'step.4.inventory.dialog.collapse_save_warning':
    'Warning: Failed to save your changes. Please try editing again or check your connection.',
  'step.4.inventory.dialog.delete_confirm': "Are you sure you want to delete '{name}'?",
  'step.4.inventory.dialog.start_over_confirm':
    "Are you sure you want to clear your {type} inventory? All your entries will be permanently deleted. Make sure you've downloaded your current inventory if you want to keep a copy.",
  'step.4.inventory.dialog.no_entries_download':
    'There are no entries to download in this inventory.',
  'step.4.inventory.dialog.download_success':
    'Successfully downloaded {count} encrypted {type} entries.',
  'step.4.inventory.dialog.download_failed': 'Failed to download inventory. Please try again.',
  'step.4.inventory.dialog.incomplete_entries':
    'There are entries in your inventory that are incomplete.',

  // Download / upload
  'step.4.inventory.file.encrypted_file': 'Encrypted Inventory File',
  'step.4.inventory.file.encrypted_files': 'Encrypted Inventory Files',
  'step.4.inventory.upload.no_file': 'No file selected',
  'step.4.inventory.upload.not_json':
    'Error: Please select a JSON file exported from Recovery Commons.',
  'step.4.inventory.upload.reading': 'Reading and decrypting file...',
  'step.4.inventory.upload.invalid_format':
    'Invalid file format. Please use a file exported from Recovery Commons.',
  'step.4.inventory.upload.type_mismatch':
    "This file contains {fileType} data, but you're trying to upload it to {currentType}. Please switch to the correct inventory tab.",
  'step.4.inventory.upload.no_data': 'No valid data found in the file.',
  'step.4.inventory.upload.save_failed': 'Failed to save imported data to your account.',
  'step.4.inventory.upload.success': 'Successfully uploaded {count} entries.',
  'step.4.inventory.upload.failed': 'Upload failed: {error}',
  'step.4.inventory.upload.unknown_error': 'Unknown error occurred while uploading the file.',

  // Step Four page — Step Five scheduling
  'step.4.inventory.schedule.title': 'Schedule Step Five with my sponsor',
  'step.4.inventory.schedule.subtext': '(while I am still working on my inventories)',
  'step.4.inventory.schedule.scheduled_full': 'Have scheduled a date and time',
  'step.4.inventory.schedule.scheduled_short': 'Scheduled date and time',
  'step.4.inventory.schedule.place_full': 'Have picked a place to meet (in-person or online)',
  'step.4.inventory.schedule.place_short': 'Picked place to meet',

  // Step Four page — action validation popup
  'step.4.inventory.validation.title': 'Action Not Complete',
  'step.4.inventory.validation.action3':
    'You need to complete all three inventories (Resentments, Fears, and Harms Done) and check both scheduling boxes to mark this action complete.',
  'step.4.inventory.validation.scheduling':
    'Click both checkboxes to confirm you have scheduled a date and time and picked a specific place.',
  'step.4.inventory.validation.ok': 'Ok',

  // Help panel content (rendered as HTML in the help dialog)
  'step.4.inventory.help.inventory': `
        <h2>🧾 How to Complete Your Step 4 Inventories</h2>
        <p>This tool helps you work through the <strong>three Step 4 inventories</strong>:</p>
        <ul>
            <li><strong>Resentments</strong></li>
            <li><strong>Fears</strong></li>
            <li><strong>Harms Done to Others</strong></li>
        </ul>
        <p>Use the <strong>tabs at the top</strong> to switch between them. As you make progress, each tab will show your completion status.</p>

        <h3>🪪 Getting Started</h3>
        <p>For each inventory:</p>
        <ol>
            <li><strong>Add entries quickly</strong> — enter names (or fears) one at a time and press <strong>Return</strong> or click <strong>"Add to List."</strong></li>
            <li><strong>Click any entry to add details</strong> — the entry expands to show editable fields. Fill them out and tab through.</li>
            <li><strong>Changes save automatically</strong> — when you leave a field or collapse an entry, your work is saved.</li>
        </ol>
        <p>When you've filled out all details for an entry, you'll see a ✅ check mark next to it. Incomplete entries will say <em>"click to see/add details."</em></p>
        <p>Once you've finished all entries for an inventory, check the <strong>"Completed"</strong> box at the top — the tab will update to show it's complete.</p>
        <blockquote>
            💡 When all three inventories are complete, you can mark this action as completed. That's a big accomplishment — completing these inventories is one of the hardest parts of working the 12 steps!
        </blockquote>

        <h3>❓ Need Help?</h3>
        <p>Not sure what a field means?<br>
        Click the <strong>blue question mark (?)</strong> next to it for a quick explanation.</p>
        <p>Your answers are saved automatically as you work, so you can close the inventory at any time and come back later.</p>

        <hr>

        <h3>📂 Manage This Inventory</h3>
        <p>Each inventory tab has three buttons at the top:</p>
        <ul>
            <li><strong>Download</strong> – Save an <strong>encrypted backup file</strong> (.json) of this inventory to your device. You'll choose where to save it and can rename the file if desired.</li>
            <li><strong>Upload</strong> – Restore a previously downloaded encrypted inventory file. Only works with files you've downloaded from Recovery Commons.</li>
            <li><strong>Start Over</strong> – Permanently delete all entries from this inventory. You'll be asked to confirm first.</li>
        </ul>
        <blockquote>
            🔐 <strong>Security & Privacy:</strong> Downloaded files are encrypted with your personal encryption key. Only you can decrypt and restore them. Your work is safe both in the cloud and on your device.
        </blockquote>
        <p><em>Note: These buttons only affect the inventory tab you're currently viewing — your other inventories remain unchanged.</em></p>

        <hr>

        <h3>📤 Share with your sponsor</h3>
        <p>Want to share your Step Four inventories with your sponsor?</p>
        <p>Use <strong>"Share with sponsor"</strong> on the Step Four or Step Five page — the summary includes your full inventories.</p>
`,
  'step.4.inventory.help.resentment_who': `
        <h3>Who do I resent?</h3>
        <p>In this part of my Step Four work, I simply list <strong>who or what I resent</strong>.</p>
        <p>I don't analyze or justify—just put down the names or things.</p>
        <p>I think broadly:</p>
        <ul>
            <li><strong>People:</strong> family, partners, exes, friends, myself, bosses, teachers, police, clergy, sponsors.</li>
            <li><strong>Institutions:</strong> marriage, church, government, legal or mental health systems.</li>
            <li><strong>Things or situations:</strong> porn, social media, alcohol, money, my job, even my phone.</li>
        </ul>
        <p>This is just the first step of my resentment inventory. Later, I'll look at <strong>why I resent them</strong>, <strong>how it affected me</strong>, and <strong>my own part</strong>.</p>
        <p>I stay honest and fearless—this is for my healing, and I'll work through the details in the next steps.</p>
`,
  'step.4.inventory.help.resentment_why': `
        <h3>Why do I resent them?</h3>
        <p>In this column, I briefly describe the specific experience or behavior that led to my resentment. I just stick to the facts—no need to overanalyze or dive into how it affected me emotionally.</p>
        <p><strong>What I do:</strong></p>
        <ul>
            <li>I write a clear, factual statement of what they did or what happened.</li>
            <li>I keep it short—like a bullet point summary.</li>
        </ul>
        <p><strong>Examples:</strong></p>
        <ul>
            <li>"They kept interrupting me during work meetings."</li>
            <li>"The clinic canceled my appointment two weeks in a row."</li>
            <li>"My ex-partner posted my private messages online."</li>
        </ul>
        <p>That's it—straightforward. This step helps me focus on exactly why the resentment exists, laying the groundwork to explore it more deeply later.</p>
`,
  'step.4.inventory.help.resentment_feeling': `
        <h3>How did that make me feel?</h3>
        <p>In this field, I briefly name the emotion(s) I experience or experienced tied to this resentment. I focus on what I felt internally—whether it was anger, hurt, betrayal, disappointment, etc. This is about capturing my emotional reaction.</p>
        <p><strong>How I do it:</strong></p>
        <ul>
            <li>I use one or two emotion words—straightforward and honest.</li>
            <li>I keep it short—just enough to convey my true feeling.</li>
        </ul>
        <p><strong>Examples:</strong></p>
        <ul>
            <li>"I felt angry and dismissed."</li>
            <li>"embarrassed, hurt"</li>
            <li>"feel betrayed"</li>
        </ul>
`,
  'step.4.inventory.help.resentment_impact': `
        <h3>What was lost, taken, or threatened inside me?</h3>
        <p>I check one or more areas of myself that were impacted by this resentment. These are about my <strong>internal parts, feelings, or needs—not external losses.</strong></p>
        <ul>
            <li><strong>Self‑esteem:</strong> My core sense of self-worth or value as a person.</li>
            <li><strong>Pride:</strong> How I believe others see me—my reputation or standing.</li>
            <li><strong>Pocketbook:</strong> My financial or material losses—money, property, possessions.</li>
            <li><strong>Personal Relationships:</strong> My connections with others—friendships, family ties, trust.</li>
            <li><strong>Sexual Relationships:</strong> My intimate or physical connections—needs, trust, closeness.</li>
            <li><strong>Emotional Security:</strong> My inner sense of stability—feeling safe, calm, or emotionally supported.</li>
            <li><strong>Ambitions:</strong> My goals, hopes, and dreams—career, personal growth, or future plans.</li>
        </ul>
`,
  'step.4.inventory.help.resentment_part': `
        <h3>What was my part in this resentment?</h3>
        <p>I honestly examine how my own attitudes or actions contributed to or prolonged this resentment. This isn't about blaming myself—it's about accepting responsibility and finding opportunities for growth.</p>
        <p><strong>I ask myself:</strong></p>
        <ul>
            <li>Was I being selfish or self-willed?</li>
            <li>Was I dishonest with myself or others?</li>
            <li>Was I fearful, defensive, or trying to control the situation?</li>
            <li>Did I gossip, shun, or refuse to communicate?</li>
            <li>Could I have handled things differently?</li>
        </ul>
        <p><strong>Examples of owning my part:</strong></p>
        <ul>
            <li>"I didn't speak up when they crossed my boundary—I held it in and then stewed in anger."</li>
            <li>"I blamed them publicly and gossiped instead of talking privately."</li>
            <li>"I refused to admit I was afraid of being rejected, so I lashed out first."</li>
        </ul>
        <p>Being honest here helps me see the role I played—so I can learn and let go. It's a powerful step toward healing.</p>
`,
  'step.4.inventory.help.fear_what': `
        <h3>What do I fear?</h3>
        <p>A <strong>Step Four Inventory</strong>—also called a <em>moral inventory</em>—is a <strong>written (or typed) self-examination</strong> used in Sex Addicts Anonymous (SAA) to honestly explore the thoughts, feelings, and behaviors that drive addiction. It covers key areas like resentments, fears, and harm done others.</p>
        <p>In this part of my Step Four work, I simply list <strong>what I fear</strong>.</p>
        <p>I don't analyze or justify—just put down the fears.</p>
        <p>I think broadly:</p>
        <ul>
            <li><strong>People:</strong> fear of rejection, fear of abandonment, fear of authority.</li>
            <li><strong>Situations:</strong> fear of failure, fear of success, fear of change.</li>
            <li><strong>Feelings:</strong> fear of emotions, fear of intimacy, fear of being alone.</li>
        </ul>
        <p>This is just the first step of my fear inventory. Later, I'll look at <strong>why I fear them</strong>, <strong>how it affected me</strong>, and <strong>my own part</strong>.</p>
        <p>I stay honest and fearless—this is for my healing, and I'll work through the details in the next steps.</p>
`,
  'step.4.inventory.help.fear_why': `
        <h3>Why do I have this fear?</h3>
        <p>In this field, I dig into the real reason behind my fear—what belief or worry is driving it. I look for the story my mind is telling me that makes this fear feel true.</p>
        <p><strong>How I fill it out:</strong></p>
        <ul>
            <li>I ask myself: What am I afraid will happen? What lie am I believing?</li>
            <li>I keep it short—just a few words or a simple phrase.</li>
        </ul>
        <p><strong>Examples:</strong></p>
        <ul>
            <li>"I fear losing my job because I think I'm not good enough."</li>
            <li>"I fear being alone because I believe no one will care about me."</li>
            <li>"I fear failing because I've let people down before."</li>
        </ul>
`,
  'step.4.inventory.help.fear_impact': `
        <h3>How does this fear affect me?</h3>
        <p>I select one or more parts of me that this fear touches—these are my inner feelings or needs that feel threatened.</p>
        <p><strong>I choose the areas it affects:</strong></p>
        <ul>
            <li><strong>Self‑esteem:</strong> How I feel about myself, my sense of worth.</li>
            <li><strong>Pride:</strong> How I think others see or judge me.</li>
            <li><strong>Pocketbook:</strong> My sense of financial or material security.</li>
            <li><strong>Personal Relationships:</strong> Trust, closeness, and support in my friendships or family.</li>
            <li><strong>Sexual Relationships:</strong> My intimate connections and trust with others.</li>
            <li><strong>Emotional Security:</strong> My inner calm and sense of safety.</li>
            <li><strong>Ambitions:</strong> My hopes, goals, and dreams for the future.</li>
        </ul>
        <p><strong>Why this matters:</strong></p>
        <p>Choosing these helps me see which parts of me feel shaken by this fear—so I can know where I may need support or healing.</p>
`,
  'step.4.inventory.help.harm_who': `
        <h3>Who did I harm?</h3>
        <p>In this field, I list <strong>everyone I've harmed</strong>, whether through big actions or small ones—by what I did or didn't do. I include anyone affected by my behavior, directly or indirectly.</p>
        <p><strong>What I do:</strong></p>
        <ul>
            <li>I write down the names of people I harmed—family, friends, partners, co-workers, acquaintances, or even people I barely know.</li>
            <li>I also include <strong>myself</strong>, recognizing that my actions or choices have often hurt me too.</li>
        </ul>
        <p><strong>Examples:</strong></p>
        <ul>
            <li>"My spouse"</li>
            <li>"My sister"</li>
            <li>"My coworker Mark"</li>
            <li>"Myself"</li>
        </ul>
`,
  'step.4.inventory.help.harm_how': `
        <h3>How did I harm them?</h3>
        <p>In this field, I briefly describe <strong>what I did or failed to do</strong> that caused harm. I keep it factual and to the point—a short summary of my action or inaction.</p>
        <p><strong>How I fill it out:</strong></p>
        <ul>
            <li>I write exactly what I did, without excuses or long stories.</li>
            <li>I keep it brief—like a bullet point.</li>
            <li>I may have <strong>multiple entries for the same person</strong>, especially for those closest to me, because I may have harmed them in different ways.</li>
        </ul>
        <p><strong>Examples:</strong></p>
        <ul>
            <li>"I ignored my brother's message when he needed help."</li>
            <li>"I lied to my partner about where I was."</li>
            <li>"I took money from my roommate without asking."</li>
        </ul>
        <p>This helps me see clearly how I've impacted others and prepares me for making things right later.</p>
`,
  'step.4.inventory.help.harm_character': `
        <h3>What part of my character caused this harm?</h3>
        <p>Here, I look at <strong>which parts of my character contributed to the harm I caused</strong>. I select one or more that fit. Being honest helps me see my patterns and take responsibility.</p>
        <p><strong>What each means:</strong></p>
        <ul>
            <li><strong>Controlling:</strong> I tried to force outcomes or people to do what I wanted.</li>
            <li><strong>Dishonest:</strong> I didn't tell the truth, hid things, or twisted facts.</li>
            <li><strong>Disengaged:</strong> I withdrew, avoided, or neglected people or situations.</li>
            <li><strong>Entitled:</strong> I believed I deserved special treatment or could do as I pleased.</li>
            <li><strong>Frightened:</strong> I acted out of fear, letting it drive my decisions.</li>
            <li><strong>Greedy:</strong> I wanted more than my share—of attention, things, or experiences.</li>
            <li><strong>Inconsiderate:</strong> I didn't think about how my actions would affect others.</li>
            <li><strong>Impulsive:</strong> I acted without thinking or caring about the consequences.</li>
            <li><strong>Lustful:</strong> I used others to satisfy my sexual desires without regard for them.</li>
            <li><strong>Self-pity:</strong> I felt sorry for myself and used that to justify harmful actions.</li>
            <li><strong>Selfish:</strong> I put my own wants and needs above everyone else's.</li>
            <li><strong>Willful:</strong> I pushed ahead with what I wanted, ignoring what was right or wise.</li>
        </ul>
`,
  'step.4.inventory.help.harm_alternative': `
        <h3>What could I have done differently?</h3>
        <p>In this field, I reflect on <strong>how I might have handled things better</strong>—actions I could have taken that would have caused less harm or resolved the situation more responsibly.</p>
        <p><strong>How I fill it out:</strong></p>
        <ul>
            <li>I keep it simple and constructive.</li>
            <li>I focus on real, practical alternatives—what I <em>could</em> have done instead.</li>
        </ul>
        <p><strong>Examples:</strong></p>
        <ul>
            <li>"I could have listened and reached out instead of walking away."</li>
            <li>"I could have been honest about my whereabouts."</li>
            <li>"I could have asked before using my roommate's money."</li>
        </ul>
        <p>Choosing these alternatives helps me see how I can grow and change in future actions.</p>
`,
  // ===== END step.4.inventory.* =====

  // ===== BEGIN step.5.* — Step Five deep workspace =====
  // Inventory presentation notes (action 3)
  'step.5.presentation.started_label': 'Date started',
  'step.5.presentation.place_label': 'Place(s)',
  'step.5.presentation.place_placeholder': "e.g. Sponsor's home, coffee shop, or Zoom",
  'step.5.presentation.reflection_label': 'How it went / how I feel',
  'step.5.presentation.reflection_placeholder': 'What the experience was like',

  // Character defect list (action 4)
  'step.5.defects.label': 'My character defects',
  'step.5.defects.examples_button': 'Character defect examples',
  'step.5.defects.first_placeholder': 'Enter a character defect...',
  'step.5.defects.next_placeholder': 'Add another character defect...',
  'step.5.examples.modal_title': 'Example character defects / shortcomings',
  // ===== END step.5.* =====

  // ===== BEGIN step.6.* — Step Six defects deep workspace =====
  // List chrome
  'step.6.defects.copy_from_step5': 'Copy from Step Five',
  'step.6.defects.copy_from_step5_title': 'Copy character defects from Step 5',
  'step.6.defects.list_title': 'My Character Defects',
  'step.6.defects.counts': '({total} total, {completed} completed)',
  'step.6.defects.click_for_details': 'click to see/add details',
  'step.6.defects.click_to_add': 'click to add',
  'step.6.defects.add_first': 'Add a character defect...',
  'step.6.defects.add_another': 'Add another character defect...',
  'step.6.defects.delete': 'Delete',
  'step.6.defects.save_failed_badge': '⚠ Save failed',

  // Defect form fields
  'step.6.field.name.label': 'Character Defect / Trait:',
  'step.6.field.name.placeholder': 'Enter character defect name...',
  'step.6.field.name.help_title': 'Get help with character defects',
  'step.6.question.1.label': '1. How does the trait hurt me?',
  'step.6.question.1.placeholder':
    'Describe how this character defect negatively impacts your life...',
  'step.6.question.2.label': '2. How does the trait serve / help me?',
  'step.6.question.2.placeholder':
    'Describe how this character defect might serve or protect you...',
  'step.6.question.3.label': '3. What would my life be like with less of this trait?',
  'step.6.question.3.placeholder':
    'Imagine and describe how your life would be different with less of this trait...',
  'step.6.help.defects_title': 'Character Defects / Traits',

  // Dialogs
  'step.6.dialog.duplicate_name': 'This character defect name already exists in your list.',
  'step.6.dialog.save_warning':
    'Warning: Failed to save your changes. Please try editing again or check your connection.',
  'step.6.dialog.delete_confirm':
    "Are you sure you want to delete '{defect}'? All answers for this character defect will be lost.",
  'step.6.dialog.copy_already_have':
    "You've already entered character defects in Step 6. To copy from Step 5, you'll need to delete your existing entries first.",
  'step.6.dialog.copy_none_found':
    'No character defects found in Step 5. You can either complete Step 5 first, or enter your character defects directly here in Step 6.',
  'step.6.dialog.copy_success':
    'Successfully copied {count} character defect(s) from Step 5. Now answer the three questions for each one.',
  'step.6.dialog.copy_failed': 'Failed to copy from Step 5. Please try again.',
  'step.6.dialog.incomplete_answers':
    'Please answer all three questions for each character defect before marking this action complete.',
  // ===== END step.6.* =====

  // ===== BEGIN step.7.* — Step Seven prayer deep workspace =====
  'step.7.prayer.traditional_heading': '7th Step Prayer (from Alcoholics Anonymous, page 76)',
  'step.7.prayer.traditional_text':
    'My Creator, I am now willing that you should have all of me, good and bad. I pray that you now remove from me every single defect of character which stands in the way of my usefulness to you and my fellows. Grant me strength as I go out from here to do your bidding. Amen',
  'step.7.prayer.traditional_source': 'AA Big Book, page 76',
  'step.7.prayer.own_words_label': 'Seventh Step Prayer in my own words',
  'step.7.prayer.own_words_placeholder':
    'Write your own version of the Seventh Step Prayer here...',
  // ===== END step.7.* =====

  // ===== BEGIN step.8.list.* — Step Eight amends list modal =====
  // Modal chrome
  'step.8.list.modal_title': 'Step Eight: List of Persons Harmed',
  'step.8.list.close': 'Close',
  'step.8.list.login_prompt': 'Please sign in to create and manage your Step Eight list.',
  'step.8.list.loading': 'Loading your list...',
  'step.8.list.description':
    'Make a list of all persons you have harmed. Your Fourth Step inventory can be helpful in creating this list.',
  'step.8.list.add_to_list': 'Add to List',
  'step.8.list.empty': 'Your list is empty. Start by adding persons you have harmed.',
  'step.8.list.your_list': 'Your List',
  'step.8.list.counts': '({total} total, {completed} completed)',
  'step.8.list.click_for_details': 'click to see/add details',
  'step.8.list.delete': 'Delete',
  'step.8.list.save_failed_badge': '⚠ Save failed',

  // Fields
  'step.8.list.name_label': "Person's name",
  'step.8.list.name_placeholder': "Person's name",
  'step.8.list.note_label': 'How they were harmed',
  'step.8.list.note_placeholder': 'Brief note about how they were harmed',
  'step.8.list.timing_label': 'Timing',
  'step.8.list.form_label': 'Form',
  'step.8.list.select_placeholder': 'Select...',
  'step.8.list.help_field_title': 'Get help with this field',
  'step.8.list.help_not_found': 'Help content not found for this field.',

  // Timing options (stored values stay English)
  'step.8.list.timing.ready_now': 'Ready Now',
  'step.8.list.timing.soon': 'Soon',
  'step.8.list.timing.future': 'Future',
  'step.8.list.timing.never': 'Never',

  // Form options (stored values stay English)
  'step.8.list.form.in_person': 'In Person',
  'step.8.list.form.phone_zoom': 'Phone/Zoom',
  'step.8.list.form.email_letter': 'Email/Letter',
  'step.8.list.form.unsent_letter': 'Unsent Letter',

  // Dialogs
  'step.8.list.dialog.ok': 'OK',
  'step.8.list.dialog.enter_name': 'Please enter a name.',
  'step.8.list.dialog.delete_confirm':
    'Are you sure you want to remove "{name}" from your list?',
  'step.8.list.dialog.session_expired_title': 'Session Expired',
  'step.8.list.dialog.session_expired_message':
    'Your session has expired. Please sign out and sign back in to continue saving changes.',
  'step.8.list.dialog.save_warning':
    'Warning: Failed to save your changes. Please try editing again or check your connection.',
  'step.8.list.dialog.close_unsaved':
    'Failed to save your changes. If you close now, you will lose your recent work. Do you want to try again or close anyway?',
  'step.8.list.dialog.close_anyway': 'Close Anyway',
  'step.8.list.dialog.try_again': 'Try Again',
  // ===== END step.8.list.* =====

  // ===== BEGIN step.9.list.* — Step Nine amends list modal =====
  // Modal chrome
  'step.9.list.modal_title': 'Step Nine: Making Amends',
  'step.9.list.close': 'Close',
  'step.9.list.login_prompt': 'Please sign in to create and manage your Step Nine amends list.',
  'step.9.list.loading': 'Loading your list...',
  'step.9.list.description':
    'Make direct amends to the people you have harmed. You can copy your list from Step 8 or add persons directly here.',
  'step.9.list.copy_from_step8': 'Copy from Step Eight',
  'step.9.list.copy_from_step8_title': 'Copy persons from Step 8',
  'step.9.list.add_to_list': 'Add to List',
  'step.9.list.empty': 'Your list is empty. Copy from Step 8 or add persons directly.',
  'step.9.list.your_list': 'Your Amends List',
  'step.9.list.counts': '({total} total, {planned} planned, {completed} completed)',
  'step.9.list.click_for_details': 'click to see/add details',
  'step.9.list.delete': 'Delete',
  'step.9.list.save_failed_badge': '⚠ Save failed',

  // Fields
  'step.9.list.name_label': "Person's name",
  'step.9.list.name_placeholder': "Person's name",
  'step.9.list.note_label': 'How they were harmed',
  'step.9.list.note_placeholder': 'Brief note about how they were harmed',
  'step.9.list.timing_label': 'Timing',
  'step.9.list.form_label': 'Form',
  'step.9.list.select_placeholder': 'Select...',
  'step.9.list.amends_plan_label': 'Amends Plan',
  'step.9.list.amends_plan_placeholder':
    'What do I want to include in this amends and how do I want to reach out (if in person or by phone or online), etc.',
  'step.9.list.amends_completed_label': 'Amends Completed',
  'step.9.list.amends_completed_hint':
    'Check when you have completed your amends to this person and reported back to your sponsor.',
  'step.9.list.amends_experience_label': 'Amends Experience',
  'step.9.list.amends_experience_placeholder': 'Record how things went, how I feel, etc.',
  'step.9.list.help_field_title': 'Get help with this field',
  'step.9.list.help_dialog_title': 'Help',
  'step.9.list.help_not_found': 'Help content not found for this field.',
  'step.9.list.help_error': 'Error loading help content.',

  // Timing options (stored values stay English)
  'step.9.list.timing.ready_now': 'Ready Now',
  'step.9.list.timing.soon': 'Soon',
  'step.9.list.timing.future': 'Future',
  'step.9.list.timing.never': 'Never',

  // Form options (stored values stay English)
  'step.9.list.form.in_person': 'In Person',
  'step.9.list.form.phone_zoom': 'Phone/Zoom',
  'step.9.list.form.email_letter': 'Email/Letter',
  'step.9.list.form.unsent_letter': 'Unsent Letter',

  // Dialogs
  'step.9.list.dialog.ok': 'OK',
  'step.9.list.dialog.enter_name': 'Please enter a name.',
  'step.9.list.dialog.delete_confirm': 'Are you sure you want to remove "{name}" from your list?',
  'step.9.list.dialog.copy_existing_entries':
    "You've already entered persons in Step 9. To copy from Step 8, you'll need to delete your existing entries first.",
  'step.9.list.dialog.copy_none_found':
    'No persons found in Step 8. You can either complete Step 8 first, or enter persons directly here in Step 9.',
  'step.9.list.dialog.copy_success':
    'Successfully copied {count} person(s) from Step 8. Now add your amends plan and work through making amends.',
  'step.9.list.dialog.copy_failed': 'Failed to copy from Step 8. Please try again.',
  'step.9.list.dialog.session_expired_title': 'Session Expired',
  'step.9.list.dialog.session_expired_message':
    'Your session has expired. Please sign out and sign back in to continue saving changes.',
  'step.9.list.dialog.save_warning':
    'Warning: Failed to save your changes. Please try editing again or check your connection.',
  'step.9.list.dialog.close_unsaved':
    'Failed to save your changes. If you close now, you will lose your recent work. Do you want to try again or close anyway?',
  'step.9.list.dialog.close_anyway': 'Close Anyway',
  'step.9.list.dialog.try_again': 'Try Again',
  // ===== END step.9.list.* =====

  // ===== BEGIN step.10.inventory.* — Step Ten nightly inventory modal =====
  // Modal chrome
  'step.10.inventory.modal_title': 'Step Ten: Daily Inventory Practice',
  'step.10.inventory.close': 'Close',
  'step.10.inventory.login_prompt': 'Please sign in to practice your daily inventory.',
  'step.10.inventory.loading': 'Loading your inventory entries...',
  'step.10.inventory.description':
    "Practice taking a nightly inventory. You can add entries for as many days as you'd like. Each entry should include a date and brief notes about your day. Complete 5 entries to finish this action.",
  'step.10.inventory.add_entry': '+ Add Entry',
  'step.10.inventory.add_entry_title': 'Add a new inventory entry',
  'step.10.inventory.empty': 'No inventory entries yet. Click "Add Entry" to get started.',
  'step.10.inventory.your_list': 'My Daily/Nightly Inventories',
  'step.10.inventory.counts': '({count} {entryWord}, {complete} of {required} complete)',
  'step.10.inventory.no_date': 'No date',
  'step.10.inventory.click_for_details': 'click to see/add details',
  'step.10.inventory.delete': 'Delete',
  'step.10.inventory.save_failed_badge': '⚠ Save failed',

  // Fields
  'step.10.inventory.date_label': 'Date',
  'step.10.inventory.note_label': 'Review of My Day',
  'step.10.inventory.note_placeholder':
    'Was I resentful, selfish, dishonest, afraid? Do I owe an apology? Do I need to talk to my sponsor about something? Was I kind, loving, and patient with others? How did I practice recovery today?',

  // Dialogs
  'step.10.inventory.dialog.ok': 'OK',
  'step.10.inventory.dialog.date_taken':
    'An entry already exists for this date. Please choose a different date.',
  'step.10.inventory.dialog.future_date': 'You cannot select a future date.',
  'step.10.inventory.dialog.delete_confirm':
    'Are you sure you want to remove the inventory entry for {date}?',
  'step.10.inventory.dialog.delete_confirm_fallback': 'this entry',
  'step.10.inventory.dialog.session_expired_title': 'Session Expired',
  'step.10.inventory.dialog.session_expired_message':
    'Your session has expired. Please sign out and sign back in to continue saving changes.',
  'step.10.inventory.dialog.save_warning':
    'Warning: Failed to save your changes. Please try editing again or check your connection.',
  'step.10.inventory.dialog.close_unsaved':
    'Failed to save your changes. If you close now, you will lose your recent work. Do you want to try again or close anyway?',
  'step.10.inventory.dialog.close_anyway': 'Close Anyway',
  'step.10.inventory.dialog.try_again': 'Try Again',
  // ===== END step.10.inventory.* =====

  // ===== BEGIN step.11.* — Step Eleven quiet time plan =====
  'step.11.plan.label': 'My Plan for Daily Quiet Time',
  'step.11.plan.placeholder':
    'Write out your plan for establishing or continuing a daily quiet time practice...',
  'step.11.plan.practiced_label': 'Practiced My Plan for 3+ days',
  // ===== END step.11.* =====

  // ===== BEGIN step.12.* — Step Twelve service plan =====
  'step.12.plan.label': 'My Plan for Service and Carrying the Message',
  'step.12.plan.placeholder':
    'Write out your plan for service and carrying the message to others...',
  // ===== END step.12.* =====

  // ===== BEGIN step.login_prompt.* — shared step page login prompt =====
  'step.login_prompt.body':
    'Sign in if you already have an account or create an account to track your progress.',
  'step.login_prompt.sign_in': 'Sign in',
  'step.login_prompt.create_account': 'Create Account',
  // ===== END step.login_prompt.* =====

  // ===== BEGIN step.share.* — shared sponsor share dialog chrome =====
  'step.share.title': 'Share your work with your sponsor',
  'step.share.close': 'Close',
  'step.share.empty_default': 'There is nothing yet to share on this step.',
  'step.share.generating': 'Preparing your summary...',
  'step.share.generating_subtext': 'This may take a moment.',
  'step.share.ready': 'Your step work progress is ready to share.',
  'step.share.view_button': 'View step work progress',
  'step.share.options_title': 'How would you like to share it?',
  'step.share.options_hint_in_app':
    'Copy link is ready immediately. View and Print open here — tap Done when finished.',
  'step.share.options_hint_window':
    'Copy link is ready immediately. Email and Print open more options first.',
  'step.share.copy_link': 'Copy link',
  'step.share.copied': 'Copied!',
  'step.share.email': 'Email',
  'step.share.email_hint': 'Choose recipient, then send',
  'step.share.print': 'Print',
  'step.share.print_hint_in_app': 'Opens here; tap Done to return',
  'step.share.print_hint_window': 'Opens preview; use print dialog',
  'step.share.email_form_title': 'Send via Email',
  'step.share.email_to_sponsor': 'Send to your sponsor:',
  'step.share.select_sponsor': 'Select Sponsor',
  'step.share.email_to_other': 'Send to another email:',
  'step.share.email_placeholder': 'email@example.com',
  'step.share.email_invalid': 'Please enter a valid email address',
  'step.share.email_to_self': 'Send to me — preview what the email looks like',
  'step.share.copied_note':
    "You'll get a copy of this email. Your sponsor will see that you were copied, and their reply will go to you.",
  'step.share.cancel': 'Cancel',
  'step.share.send_email': 'Send Email',
  'step.share.sending': 'Sending...',
  'step.share.expiry_note':
    'For privacy, this preview summary link expires 7 days after creation ({date}).',
  'step.share.error_generating': 'Error creating summary. Please try again.',
  'step.share.popup_blocked': 'Please allow pop-ups to preview or print the summary.',
  'step.share.print_failed': 'Could not open the print dialog. Try again or use Copy link.',
  'step.share.copy_failed': 'Failed to copy link. Please try again.',
  'step.share.email_sent_self': 'Preview email sent to {email}!',
  'step.share.email_sent': 'Successfully sent to {email}!',
  'step.share.email_failed': 'Failed to send email. Please try again.',
  'step.share.preview_done': 'Done',
  'step.share.preview_title': 'Step work progress',
  'step.share.preview_aria': 'Step work progress preview',
  'step.share.preview_hint': 'Tap Done to return to sharing options.',
  'step.share.preview_back': '← Back to Recovery Commons',
  'step.share.preview_close_tab': 'Close this tab when you are finished',
  // ===== END step.share.* =====

  // ===== Header menu & footer =====
  'ui.initializing': 'Initializing...',
  'nav.menu.title': 'Menu',
  'nav.menu.toggle_aria': 'Toggle menu',
  'nav.menu.profile': 'Profile',
  'nav.menu.invite': 'Invite',
  'nav.menu.feedback': 'Feedback',
  'nav.menu.help': 'Help',
  'nav.menu.start_over': 'Save & Start over',
  'nav.menu.backup': 'Backup & Restore',
  'nav.menu.about': 'About',
  'nav.menu.privacy': 'Privacy',
  'nav.menu.sign_out': 'Sign out',
  'nav.sign_in_create': 'Sign in / Create account',
  'nav.sign_in': 'Sign in',
  'nav.workshop': 'Workshop',
  'nav.privacy_title': 'Your privacy matters',
  'nav.footer.feedback': 'Feedback',
  'nav.footer.about': 'About',
  'nav.footer.privacy': 'Privacy Policy',
  'nav.footer.version': 'Version {version}',
  'nav.footer.rights': 'All rights reserved',

  // ===== Sign in, create account, password reset =====
  'auth.form.sign_in_title': 'Sign In',
  'auth.form.create_title': 'Create Account',
  'auth.form.reset_title': 'Reset Password',
  'auth.form.close': 'Close',
  'auth.form.email': 'Email',
  'auth.form.password': 'Password',
  'auth.form.confirm_password': 'Confirm Password',
  'auth.form.show_password': 'Show password',
  'auth.form.hide_password': 'Hide password',
  'auth.form.remember_email': 'Remember my email',
  'auth.form.processing': 'Processing...',
  'auth.form.send_reset_code': 'Send Reset Code',
  'auth.form.create_button': 'Create Account',
  'auth.form.sign_in_button': 'Sign In',
  'auth.form.back_to_sign_in': 'Back to Sign In',
  'auth.form.have_account': 'Already have an account? Sign In',
  'auth.form.need_account': 'Need an account? Create One',
  'auth.form.forgot_password': 'Forgot your password?',
  'auth.form.passwords_mismatch': 'Passwords do not match',
  'auth.form.created_signin_failed': 'Account created but sign-in failed. Please try signing in manually.',
  'auth.form.failed': 'Authentication failed',
  'auth.invite.banner': '{name} invited you to join Recovery Commons',
  'auth.invite.banner_as_sponsor': '{name} invited you to join Recovery Commons as their sponsor',
  'auth.invite.banner_as_sponsee': '{name} invited you to join Recovery Commons as their sponsee',
  'auth.display_name.label': 'Display name (first name and last initial)',
  'auth.display_name.placeholder': 'e.g. John D',
  'auth.display_name.hint':
    'How you appear to other members — first name and last initial only (e.g. John D).',
  'auth.reset.title': 'Reset Your Password',
  'auth.reset.code_sent_to': "We've sent a reset code to:",
  'auth.reset.check_email': 'Please check your email and enter the 6-digit code below.',
  'auth.reset.code_label': 'Reset Code',
  'auth.reset.code_placeholder': 'Enter 6-digit code',
  'auth.reset.new_password': 'New Password',
  'auth.reset.new_password_placeholder': 'Enter new password',
  'auth.reset.password_min': 'Password must be at least 8 characters long',
  'auth.reset.confirm_new_password': 'Confirm New Password',
  'auth.reset.confirm_new_password_placeholder': 'Confirm new password',
  'auth.reset.resetting': 'Resetting Password...',
  'auth.reset.submit': 'Reset Password',
  'auth.reset.success': 'Password reset successfully! You can now sign in with your new password.',
  'auth.reset.failed': 'Password reset failed',

  // ===== Home page =====
  // home.tagline / section_heading / bullet1-3 mirror the English set in Admin → Home.
  'home.tagline': 'Work your steps. Support your recovery. Give your meeting a home page.',
  'home.section_heading': "What you'll find here",
  'home.bullet1':
    'A place to track your 12 step work with guided actions, videos, and progress you own\u2014so you keep your momentum.',
  'home.bullet2': 'Our current SAA 12 Step Workshop home page',
  'home.bullet3':
    'Privacy and control: Your step work is encrypted and only you see it; you and your meeting control who sees meeting information.',
  'home.saa_subtitle': 'Sex Addicts Anonymous',
  'home.open_saa_hint':
    'Open SAA to work your steps. Use the Workshop link in the header to learn about the 2026 step workshop.',
  'home.more_programs': 'More programs (e.g. AA) coming later.',

  // ===== Verify email page =====
  'auth.verify.title': 'Verify Your Email',
  'auth.verify.sent_to': 'We sent a verification code to:',
  'auth.verify.check_email': 'Check your email (including spam/junk folder)',
  'auth.verify.from_label': 'The email is from',
  'auth.verify.spam_hint': "If you don't see it in your inbox, please check your spam or junk folder.",
  'auth.verify.code_label': 'Verification Code',
  'auth.verify.code_placeholder': 'Enter the 6-digit code from your email',
  'auth.verify.verifying': 'Verifying...',
  'auth.verify.submit': 'Verify Email',
  'auth.verify.no_code': "Didn't receive the code?",
  'auth.verify.resend': 'Resend verification code',
  'auth.verify.resend_short': 'Resend Code',
  'auth.verify.resent': 'Verification code resent! Please check your email.',
  'auth.verify.code_required': 'Verification code and email are required',
  'auth.verify.email_required': 'Email address is required',
  'auth.verify.verified_sign_in': 'Email verified successfully! You can now sign in to your account.',
  'auth.verify.verified_logged_in': 'Email verified successfully! You are now logged in.',
  'auth.verify.success_title': 'Email Verified & Logged In!',
  'auth.verify.success_body': "Your email has been successfully verified and you're now logged in. Redirecting…",
  'auth.verify.continue': 'Continue',
  'auth.verify.failed_title': 'Verification Failed',
  'auth.verify.go_to_sign_in': 'Go to Sign In',

  // ===== Sign-in / account server messages =====
  'auth.server.network_error': 'Network error. Please try again.',
  'auth.server.invalid_email': 'Invalid email format',
  'auth.server.user_exists': 'An account with this email already exists.',
  'auth.server.send_failed': 'Failed to send verification email',
  'auth.server.create_failed': 'Failed to create account',
  'auth.server.created_can_log_in': 'Account created successfully. You can now log in.',
  'auth.server.created_check_email': 'Account created successfully. Please check your email for verification.',
  'auth.server.user_not_found': 'User not found',
  'auth.server.already_verified': 'Email is already verified',
  'auth.server.invalid_code': 'Invalid verification code',
  'auth.server.code_expired': 'Verification code expired. Request a new code.',
  'auth.server.verify_failed': 'Verification failed',
  'auth.server.verified': 'Email verified successfully',
  'auth.server.invalid_credentials': 'Invalid email or password',
  'auth.server.inactive': 'Account is not active',
  'auth.server.not_verified': 'Email not verified. Please check your email for verification.',
  'auth.server.login_failed': 'Login failed',
  'auth.server.login_ok': 'Login successful',
  'auth.server.reset_sent': 'If an account with this email exists, a password reset code has been sent.',
  'auth.server.reset_request_failed': 'Failed to process password reset request',
  'auth.server.invalid_reset_code': 'Invalid reset code',
  'auth.server.reset_code_expired': 'Reset code expired. Request a new code.',
  'auth.server.reset_ok': 'Password reset successfully',
  'auth.server.resend_sent': 'If an account exists with this email, a confirmation code has been sent',
  'auth.server.resend_ok': 'Confirmation code resent successfully',
  'auth.server.resend_failed': 'Failed to resend confirmation code',

  // ===== Account emails (sent by the server: verification + password reset codes) =====
  // Keep in sync with lambda/email_i18n.py. {name} is the member's name.
  'email.auth.verify.subject': 'Verify Your Recovery Commons Account',
  'email.auth.reset.subject': 'Password Reset Code',
  'email.auth.greeting': 'Hello {name}!',
  'email.auth.greeting_no_name': 'Hello!',
  'email.auth.verify.instructions': 'Please use the following code to verify your email address:',
  'email.auth.reset.instructions': 'Please use the following code to reset your password:',
  'email.auth.expires': 'This code will expire in 15 minutes.',
  'email.auth.ignore': "If you didn't request this, please ignore this email.",
  'email.auth.footer': 'Recovery Commons - Supporting your recovery journey',

  // ===== Invitation panel (shown to invited members right after signup) =====
  'auth.invite.panel.title': 'Invitations',
  'auth.invite.panel.pending_title': 'Pending Invitations ({count})',
  'auth.invite.panel.loading': 'Loading invitations...',
  'auth.invite.panel.empty': 'No pending invitations',
  'auth.invite.panel.load_failed': 'Failed to load invitations',
  'auth.invite.panel.accept_failed': 'Failed to accept invitation',
  'auth.invite.panel.decline_failed': 'Failed to decline invitation',
  'auth.invite.panel.as_sponsor': '{name} invited you to be their sponsor',
  'auth.invite.panel.as_sponsee': '{name} invited you to be their sponsee',
  'auth.invite.panel.as_friend': '{name} invited you to connect as a recovery friend',
  'auth.invite.panel.personal_message': 'Personal message:',
  'auth.invite.panel.program': 'Program:',
  'auth.invite.panel.decline': 'Decline',
  'auth.invite.panel.declining': 'Declining...',
  'auth.invite.panel.accept': 'Accept',
  'auth.invite.panel.accepting': 'Accepting...',

  // ===== Pick your program (after signup) =====
  'onboarding.title': 'Pick your primary recovery program',
  'onboarding.note': 'Choose the recovery program that best fits your needs. You can change this anytime.',
  'onboarding.assessment_title': 'Your Assessment Results',
  'onboarding.assessment_note': 'Based on your assessment, we recommend starting with {program}. You can change this later.',
  'onboarding.assessment_note_generic':
    'Based on your assessment, we recommend starting with a recovery program. You can change this later.',
  'onboarding.recommended_programs': 'Recommended Programs:',
  'onboarding.primary': 'Primary',
  'onboarding.recommended': 'Recommended',
  'onboarding.skip': 'Skip for now',
  'onboarding.notice': 'Notice',
  'onboarding.ok': 'OK',
  'onboarding.save_failed': 'Unable to save your program preference. You can set it later in your profile.',
  'onboarding.program.aa': 'AA - Alcoholics Anonymous',
  'onboarding.program.saa': 'SAA - Sex Addicts Anonymous',
  'onboarding.program.na': 'NA - Narcotics Anonymous',
  'onboarding.program.oa': 'OA - Overeaters Anonymous',
  'onboarding.program.ga': 'GA - Gamblers Anonymous',
  'onboarding.program.da': 'DA - Debtors Anonymous',
  'onboarding.program.coda': 'CoDA - Co-Dependents Anonymous',
  'onboarding.program.ca': 'CA - Cocaine Anonymous',
  'onboarding.program.not_sure': 'Not sure yet',

  // ===== Shared dialog buttons & sign out =====
  'dialog.yes': 'Yes',
  'dialog.no': 'No',
  'dialog.ok': 'OK',
  'dialog.cancel': 'Cancel',
  'dialog.close': 'Close',
  'dialog.save': 'Save',
  'dialog.delete': 'Delete',
  'dialog.help': 'Help',
  'dialog.close_help': 'Close help dialog',
  'auth.sign_out.title': 'Signing out...',
  'auth.sign_out.message': 'You have been successfully signed out. Returning to home page.',
  'auth.callback.session_expired': 'Sign-in session expired. Please try signing in again.',
  'auth.callback.unable_to_complete': 'Unable to complete login. Please try again.',
  'auth.callback.verification_failed': 'Sign-in verification failed. Please try again.',
  'auth.callback.auth_failed': 'Authentication failed. Please try again.',
  'auth.callback.generic': 'An error occurred during login. Please try again.',
  'auth.callback.title': "Couldn't sign you in",

  // ===== Meetings dialogs (out of scope for translation: see OUT_OF_SCOPE_PREFIXES in sections.ts) =====
  'meetings.dialog.remove_home_title': 'Remove from My Meetings',
  'meetings.dialog.remove_home_message':
    'Remove this meeting from your My Meetings list? You can add it again from the meeting page.',
  'meetings.dialog.remove': 'Remove',
  'meetings.dialog.remove_member_title': 'Remove from meeting',
  'meetings.dialog.remove_member_message':
    'Remove {name} from this meeting? They will need to add themselves again (or use the join key) to rejoin.',
  'meetings.dialog.remove_member_confirm': 'Remove from meeting',

  // ===== Step 8 & self-assessment dialogs =====
  'step.8.alert.complete_one_person':
    'Please complete at least one person entry (name, note, timing, and form) before marking this action complete.',
  'self_assess.safety.title': 'Safety & Support',
  'self_assess.safety.content':
    "## If you're in immediate danger or deep distress:\n\n**Call 988** - National Suicide & Crisis Lifeline (US)\n**Call 1-800-662-HELP** - SAMHSA National Helpline (24/7)\n\n## About this assessment:\n\nThis self-assessment is designed to help you reflect on patterns in your life. It's not a diagnosis or medical advice - only you can decide if a recovery program is right for you.\n\n**Your privacy matters:** All answers stay private on this device unless you choose to save them.\n\n**Take your time:** There are no right or wrong answers. Be honest with yourself about what you're experiencing.",

  // ===== Profile & sponsors (ProfileModal) =====
  'account.title': 'Profile',
  'account.tab.profile': 'Profile',
  'account.tab.sponsorship': 'Sponsorship',
  'account.profile.load_failed': 'Failed to load profile data',
  'account.profile.update_failed': 'Failed to update profile',
  'account.profile.loading': 'Loading profile...',
  'account.profile.saving': 'Saving...',
  'account.profile.save_changes': 'Save Changes',
  'account.profile.edit': 'Edit Profile',
  'account.profile.no_data': 'No profile data available',
  'account.field.email': 'Email',
  'account.field.phone': 'Phone',
  'account.field.phone_placeholder': 'e.g. (555) 123-4567',
  'account.field.phone_help': 'Used for meeting and workshop phone lists when you opt in.',
  'account.field.not_available': 'Not available',
  'account.field.not_provided': 'Not provided',
  'account.field.member_since': 'Member Since',
  'account.field.last_sign_in': 'Last sign-in',
  'account.status.confirmed': '✓ Confirmed',
  'account.status.pending': 'Pending',
  'account.sponsor.fill_all_fields': 'Please fill in all fields (Program, Name, and Email).',
  'account.sponsor.invalid_email': 'Please enter a valid email address.',
  'account.sponsor.added': 'Sponsor added successfully!',
  'account.sponsor.add_failed': 'Failed to add sponsor. Please try again.',
  'account.sponsor.remove_confirm': 'Are you sure you want to remove {name} ({email}) as your {program} sponsor?',
  'account.sponsor.removed': 'Sponsor removed successfully.',
  'account.sponsor.remove_failed': 'Failed to remove sponsor. Please try again.',
  'account.sponsor.section_title': 'My Sponsors',
  'account.sponsor.section_description': 'People who sponsor you. Use Share with sponsor on step pages to send them summaries.',
  'account.sponsor.invite_button': '+ Invite Sponsor',
  'account.sponsor.loading': 'Loading sponsors...',
  'account.sponsor.empty': 'No sponsors added yet.',
  'account.sponsor.remove_title': 'Remove sponsor',
  'account.sponsee.remove_confirm': 'Are you sure you want to remove {name} ({email}) as your {program} sponsee?',
  'account.sponsee.removed': 'Sponsee removed successfully.',
  'account.sponsee.remove_failed': 'Failed to remove sponsee. Please try again.',
  'account.sponsee.section_title': 'My Sponsees',
  'account.sponsee.section_description': 'People you sponsor. They share progress with you using Share with sponsor on their step pages.',
  'account.sponsee.invite_button': '+ Invite Sponsee',
  'account.sponsee.loading': 'Loading sponsees...',
  'account.sponsee.empty': 'No sponsees added yet.',
  'account.sponsee.remove_title': 'Remove sponsee',

  // ===== Invitations (InviteManagement) =====
  'invite.title': 'Invite Someone to Recovery Commons',
  'invite.sent_success': '✓ Invitation sent successfully!',
  'invite.form.required_fields': 'Please fill in all required fields',
  'invite.form.send_failed': 'Failed to send invitation',
  'invite.form.email_label': 'Email Address',
  'invite.form.email_placeholder': 'their.email@example.com',
  'invite.form.relationship_label': 'Relationship',
  'invite.form.relationship_my_sponsor': 'My Sponsor',
  'invite.form.relationship_my_sponsee': 'My Sponsee',
  'invite.form.program_label': 'Program',
  'invite.form.message_label': 'Personal Message (Optional)',
  'invite.form.message_placeholder': 'Add a personal message to your invitation...',
  'invite.form.sending': 'Sending...',
  'invite.form.send': 'Send Invitation',
  'invite.relationship.sponsor': 'Sponsor',
  'invite.relationship.sponsee': 'Sponsee',
  'invite.relationship.friend': 'Recovery Friend',
  'invite.status.pending': 'Pending',
  'invite.status.accepted': 'Accepted',
  'invite.status.declined': 'Declined',
  'invite.program.ma': 'MA - Marijuana Anonymous',
  'invite.program.aca': 'ACA - Adult Children of Alcoholics',
  'invite.program.coda': 'CoDA - Codependents Anonymous',
  'invite.history.title': 'Sent Invitations',
  'invite.history.load_failed': 'Failed to load invitation history',
  'invite.history.loading': 'Loading history...',
  'invite.history.empty': 'No invitations sent yet. Send your first invitation above!',

  // ===== Meeting join preferences (JoinPreferencesModal; out of scope for translation) =====
  'meetings.join_prefs.title': 'Add you to this meeting',
  'meetings.join_prefs.display_label': 'How I appear on the member list',
  'meetings.join_prefs.display.name_only': 'Name only (first name + last initial)',
  'meetings.join_prefs.display.name_and_location': 'Name and location',
  'meetings.join_prefs.display.name_and_phone': 'Name and phone',
  'meetings.join_prefs.display.hidden': 'Hidden (don’t show me on the list)',
  'meetings.join_prefs.include_phone_list': 'Include my phone on the printed phone list (when I share phone)',
  'meetings.join_prefs.email_notifications': 'Send me meeting emails (announcements and reminders)',
  'meetings.join_prefs.sponsor_label': 'Willing to sponsor in this meeting?',
  'meetings.join_prefs.sponsor.temporary': 'Temporarily',
  'meetings.join_prefs.privacy_note': 'Only other members of this meeting can see your choices.',
  'meetings.join_prefs.adding': 'Adding…',
  'meetings.join_prefs.submit': 'Add me to this meeting',

  // ===== Share & Print dialog =====
  'share.title': 'Share & Print',
  'share.title_sponsee': "{name}'s Current Progress",
  'share.subtitle': 'Share your current progress',
  'share.subtitle_sponsee': 'View and create reports for your sponsee ({program})',
  'share.select_title': 'What would you like to share?',
  'share.select_title_sponsee': 'What would you like to view?',
  'share.help.title': 'Share Your Progress',
  'share.help.title_sponsee': 'Viewing Sponsee Progress',
  'share.help.content':
    'Create a report to view on screen, print, or send to your sponsor. Reports are only available for 7 days for your privacy.',
  'share.help.content_sponsee':
    "You can view {name}'s current progress. Reports are generated fresh each time and are only available for 7 days for privacy.",
  'share.option.12_step_status.title': '12-Step Status',
  'share.option.12_step_status.description': 'Your current progress on the 12 steps',
  'share.option.step_four_inventories.title': 'Step Four Inventories',
  'share.option.step_four_inventories.description': 'Your 3 current inventories',
  'share.document_name.12_step_status': 'SAA 12-Step Status',
  'share.document_name.step_four_inventories': 'Step Four Inventories',
  'share.generating': 'Generating your report...',
  'share.generating_subtext': 'This may take a moment.',
  'share.report_ready': 'Your {document} report is ready!',
  'share.preview_report': 'Preview Report',
  'share.how_to_share': 'How would you like to share it?',
  'share.button.share': 'Share',
  'share.button.copy_link': 'Copy Link',
  'share.button.copied': 'Copied!',
  'share.button.email': 'Email',
  'share.email.title': 'Send via Email',
  'share.email.to_sponsor': 'Send to your sponsor:',
  'share.email.no_sponsors': 'No sponsors configured',
  'share.email.select_sponsor': 'Select Sponsor',
  'share.email.to_other': 'Send to another email:',
  'share.email.placeholder': 'email@example.com',
  'share.email.invalid': 'Please enter a valid email address',
  'share.email.send': 'Send Email',
  'share.email.sending': 'Sending...',
  'share.back_to_choose': 'Back to choose different report',
  'share.alert.no_steps':
    "There is nothing yet to share. Please come back when you have started working SAA's 12 steps.",
  'share.alert.no_steps_sponsee': "{name} hasn't started working on SAA's 12 steps yet.",
  'share.alert.no_inventories':
    'There is nothing in your inventories yet. Please come back when you have started working on your Step Four inventories.',
  'share.alert.no_inventories_sponsee': "{name} hasn't started their Step Four inventories yet.",
  'share.alert.create_failed': 'Error creating document. Please try again.',
  'share.alert.native_share_failed': 'Share failed. Please try copy link instead.',
  'share.alert.copy_failed': 'Failed to copy link. Please try again.',
  'share.alert.email_sent': 'Successfully sent to {email}!',
  'share.alert.email_failed': 'Failed to send email. Please try again.',

  // ===== Step history (timeline) dialog =====
  'history.title': '{stepTitle} Timeline',
  'history.empty':
    'As you start completing the actions to work this step, you will see your progress timeline tracked here',
  'history.timeline.started': '{stepTitle} started',
  'history.timeline.completed': '{stepTitle} completed',
  'history.in_progress': 'In progress',
  'history.edit_button': 'Edit',
  'history.edit_dates_tooltip': 'Edit dates',
  'history.duration_one': 'Duration: {count} day',
  'history.duration_other': 'Duration: {count} days',
  'history.edit_dates_title': 'Edit Step Dates',
  'history.start_date_label': 'Start Date:',
  'history.completed_date_label': 'Completion Date:',
  'history.saving': 'Saving...',
  'history.alert.start_date_required': 'Please enter a start date',
  'history.alert.start_date_invalid': 'Please enter a valid start date',
  'history.alert.start_date_future': 'Start date cannot be in the future',
  'history.alert.completed_date_invalid': 'Please enter a valid completion date',
  'history.alert.completed_date_future': 'Completion date cannot be in the future',
  'history.alert.completed_before_start': 'Completion date must be after start date',
  'history.alert.dates_updated': 'Dates updated successfully',
  'history.alert.update_failed': 'Failed to update dates',

  // ===== Feedback dialog =====
  'feedback.title': 'Feedback Form',
  'feedback.close_aria': 'Close feedback form',
  'feedback.subtitle': 'Thank you for helping to improve Recovery Commons',
  'feedback.success.title': 'Thank you!',
  'feedback.success.message': 'Your feedback has been submitted successfully.',
  'feedback.success.copy_emailed': 'A copy has been emailed to {email}',
  'feedback.type_label': 'Type of feedback:',
  'feedback.type.general': 'General',
  'feedback.type.bug': 'Error / Bug',
  'feedback.type.idea': 'Idea',
  'feedback.type.translation': 'Language / Translation',
  'feedback.text_label': 'Feedback:',
  'feedback.text_placeholder': 'Please share your feedback...',
  'feedback.text_placeholder_translation':
    "Tell us what doesn't read right and what you'd suggest. Copying the exact words helps.",
  'feedback.email_copy': 'Email me a copy of my feedback',
  'feedback.submit': 'Submit Feedback',
  'feedback.submitting': 'Submitting...',
  'feedback.submit_failed': 'Failed to submit feedback',
  'feedback.info.user': 'User:',
  'feedback.info.location': 'Location in app:',
  'feedback.info.device': 'Device & Browser:',
  'feedback.info.device_value': '{deviceType} - {browser} {browserVersion} on {os}',

  // ===== About dialog =====
  'about.title': 'About Recovery Commons',
  'about.load_error': 'Unable to load content at this time.',
  'about.toc_aria': 'About contents',
  'about.toc_title': 'Contents',
  'about.version_history': 'Version History',
  'about.loading_content': 'Loading about content...',
  'about.show_earlier_versions': 'Show earlier versions',
  'about.show_fewer_versions': 'Show fewer versions',
  'about.deployed': 'Deployed: {date}',
  'about.loading_version': 'Loading version information...',

  // ===== Video dialog =====
  'video.default_title': 'Video Guide',
  'video.loading': 'Loading video...',
  'video.iframe_title': 'Recovery Commons Video',

  // ===== Save & Start over (modal wrapper) =====
  'start_over.title': 'Save & Start over',

  // ===== Saved copies of step work (StepWorkArchiveTab) =====
  'archive.default_save_name': 'Before workshop – {date}',
  'archive.summary.no_work': 'No step work saved yet',
  'archive.summary.getting_started_done': 'Getting Started done',
  'archive.summary.getting_started_started': 'Getting Started started',
  'archive.summary.steps_completed': '{count} of 12 steps completed',
  'archive.summary.working_through': 'working through the steps',
  'archive.error.load_failed': 'Could not load your step work. Please try again.',
  'archive.error.name_required': 'Please enter a name for this saved copy.',
  'archive.error.save_failed': 'Could not save your work. Please try again.',
  'archive.error.restore_failed': 'Could not restore. Please try again.',
  'archive.error.delete_failed': 'Delete failed.',
  'archive.dialog.nothing_to_save_title': 'Nothing to save yet',
  'archive.dialog.nothing_to_save_message':
    'You do not have any step work in Recovery Commons yet. When you do, come back here before starting again — we will save a copy for you.',
  'archive.dialog.limit_title': 'Saved copy limit reached',
  'archive.dialog.limit_message':
    'You can keep up to {max} named saves. Delete one from the list below to save another.',
  'archive.dialog.fresh_start_title': 'You are ready for a fresh start',
  'archive.dialog.fresh_start_message':
    'Your prior step work is saved as "{name}". Your steps are reset — head to Getting Started when you are ready.',
  'archive.dialog.open_failed_title': 'Could not open saved copy',
  'archive.dialog.open_failed_message': 'Please try again.',
  'archive.dialog.restore_title': 'Switch back to this saved work?',
  'archive.dialog.restore_message':
    'Your current step work will be replaced with "{name}". If you have unsaved work now, we will save a copy automatically first.',
  'archive.dialog.restore_confirm': 'Switch to this copy',
  'archive.dialog.restored_title': 'Saved work restored',
  'archive.dialog.restored_message': '"{name}" is now your active step work.',
  'archive.dialog.delete_title': 'Delete this saved copy?',
  'archive.dialog.delete_message': 'Permanently delete "{name}"? This cannot be undone.',
  'archive.signed_out': 'Sign in to save your step work and start over with a clean workspace.',
  'archive.loading': 'Loading…',
  'archive.hero.title': 'Working the steps again?',
  'archive.hero.lead':
    'Many people come back for a workshop or a new round in recovery. If you already have step work in Recovery Commons, save it here first — we keep a copy for you, then reset your steps so you can begin fresh.',
  'archive.hero.current_work': 'Your current step work',
  'archive.hero.save_and_start_over': 'Save my work & start over',
  'archive.hero.limit_hint': 'You have {max} saved copies. Delete one below to save another.',
  'archive.form.name_label': 'Name this saved copy',
  'archive.form.name_hint': 'So you can find it later — e.g. “Before 2026 workshop”',
  'archive.form.what_happens_save': 'We save everything you have done so far under this name',
  'archive.form.what_happens_reset': 'Your steps reset to blank — ready for a new round',
  'archive.form.what_happens_switch_back': 'You can switch back to this copy anytime from the list below',
  'archive.form.saving': 'Saving…',
  'archive.ready.with_archives': 'Your steps are clear — you are ready to begin a new round.',
  'archive.ready.no_archives':
    'Your steps are clear. When you have step work to save, come here before your next round.',
  'archive.ready.go_getting_started': 'Go to Getting Started',
  'archive.list.title': 'Previously saved step work',
  'archive.list.intro_active':
    'After you start over, your prior work appears here. You can switch back anytime.',
  'archive.list.intro_ready': 'Switch back to an earlier round, or delete copies you no longer need.',
  'archive.list.auto_section': 'Automatic saves',
  'archive.list.auto_tag': 'Saved automatically',
  'archive.list.details': 'Details',
  'archive.list.switch': 'Switch to this',
  'archive.list.limit_note': 'Up to {max} named saves ({count} used)',
  'archive.view.default_title': 'Saved copy',
  'archive.view.saved_meta': 'Saved {date} · {summary}',
  'archive.view.completed_steps': 'Completed steps: {steps}',
  'archive.view.inventories_included': 'Inventories included ({count})',

  // ===== Backup & Restore (legacy BackupRestoreModal) =====
  'backup.title': 'Backup & Restore',
  'backup.eol.title': 'Backup & Restore is being replaced.',
  'backup.eol.body':
    'To save your step work and start over, use {action} in the menu — we keep a copy on the server for you.',
  'backup.eol.action': 'Save & Start over',
  'backup.eol.open_button': 'Open Save & Start over',
  'backup.select_program_track': 'Select Program & Track',
  'backup.more_programs_soon': 'More programs coming soon',
  'backup.choose_action': 'Choose Action',
  'backup.mobile_tips': 'Mobile Tips',
  'backup.processing': 'Processing...',
  'backup.backup.title': 'Backup Progress',
  'backup.backup.intro':
    'This will backup your progress for {label}, including completed steps, inventory items, and personal notes.',
  'backup.backup.filename_label': 'Filename:',
  'backup.backup.filename_placeholder': 'Enter filename',
  'backup.backup.location_label': 'Location Selection:',
  'backup.backup.location_text':
    'You will be prompted to select a folder where you want to store the backup.',
  'backup.backup.encryption_label': 'Encryption:',
  'backup.backup.encryption_text':
    'The backup will be encrypted and can only be read by this application for future restore operations.',
  'backup.backup.clear_after': 'Clear data after backup',
  'backup.backup.error.filename_required': 'Please enter a filename',
  'backup.backup.error.check_failed': 'Unable to check current data. Please try again.',
  'backup.backup.completed': 'Backup completed',
  'backup.backup.completed_and_cleared': 'Backup completed and data in application cleared',
  'backup.backup.completed_clear_failed': 'Backup completed, but failed to clear data: {error}',
  'backup.backup.failed': 'Backup failed',
  'backup.backup.failed_with_reason': 'Backup failed: {error}',
  'backup.no_data.title': 'No Data to Backup',
  'backup.no_data.message':
    'There is no data to backup. Please complete some steps or add inventory items first.',
  'backup.restore.title': 'Restore Progress',
  'backup.restore.intro': 'This will load progress data for {label} from a backup file.',
  'backup.restore.current_data_warning':
    "You have current progress and content for {label}. If you don't want this import to overwrite it, choose cancel and use backup.",
  'backup.restore.failed': 'Restore failed',
  'backup.restore.failed_with_reason': 'Restore failed: {error}',
  'backup.restore.error.overwrite_required':
    'Please check "Overwrite existing progress" to continue, or clear your current data first',
  'backup.restore.error.clear_failed': 'Failed to clear current data',
  'backup.restore.result.heading': 'Restore completed successfully!',
  'backup.restore.result.heading_cleared': 'Data cleared and restore completed successfully!',
  'backup.restore.result.restored_one': 'Successfully restored: {count} item',
  'backup.restore.result.restored_other': 'Successfully restored: {count} items',
  'backup.restore.result.warnings_one': 'Restored with warnings: {count} item',
  'backup.restore.result.warnings_other': 'Restored with warnings: {count} items',
  'backup.restore.result.skipped_one': 'Skipped: {count} item (could not be matched)',
  'backup.restore.result.skipped_other': 'Skipped: {count} items (could not be matched)',
  'backup.restore.completed': 'Restore completed successfully',
  'backup.restore.completed_cleared': 'Data cleared and restored successfully',
  'backup.restore.clear_and_restore_failed': 'Clear and restore failed: {error}',
  'backup.preview.title': 'Restore Preview',
  'backup.preview.file_contents': 'File Contents:',
  'backup.preview.program': 'Program:',
  'backup.preview.track': 'Track:',
  'backup.preview.backup_date': 'Backup Date:',
  'backup.preview.steps_with_data': 'Steps with Data:',
  'backup.preview.inventories': 'Inventories:',
  'backup.preview.unknown': 'Unknown',
  'backup.preview.none': 'None',
  'backup.preview.inventories_none': 'None found',
  'backup.preview.entries_one': '{count} entry',
  'backup.preview.entries_other': '{count} entries',
  'backup.preview.entries_across': '{count} entries across {inventories} inventories',
  'backup.preview.overwrite_existing': 'Overwrite existing progress',
  'backup.preview.clear_and_restore': 'Clear & Restore',
  'backup.preview.restoring': 'Restoring...',
  'backup.preview.restore_data': 'Restore Data',
  'backup.clear.title': 'Clear Progress',
  'backup.clear.intro':
    'This will permanently delete all your progress data for {label}. This action cannot be undone.',
  'backup.clear.warning':
    'You have current data for {label}. Make sure you have backed up your data first.',
  'backup.clear.confirm_label': 'Type "{word}" to confirm:',
  'backup.clear.error.confirm_required': 'Please type "{word}" to confirm',
  'backup.clear.success': 'Data cleared successfully',
  'backup.clear.failed': 'Clear failed',
  'backup.clear.failed_with_reason': 'Clear failed: {error}',
  'backup.clear.clearing': 'Clearing...',
  'backup.clear.clear_all': 'Clear All Data',

  // ===== Relative dates ("2 weeks and 3 days ago") =====
  'time.today': 'today',
  'time.yesterday': 'yesterday',
  'time.ago': '{duration} ago',
  'time.and': '{a} and {b}',
  'time.day_one': '{count} day',
  'time.day_other': '{count} days',
  'time.week_one': '{count} week',
  'time.week_other': '{count} weeks',
  'time.month_one': '{count} month',
  'time.month_other': '{count} months',
  'time.year_one': '{count} year',
  'time.year_other': '{count} years',

  // ===== Saved copies list & backup helpers =====
  'archive.summary.snapshot': 'Step work snapshot',
  'archive.summary.getting_started': 'Getting Started',
  'archive.summary.steps_completed_one': '{count} step completed',
  'archive.summary.steps_completed_other': '{count} steps completed',
  'archive.summary.inventories_saved': 'inventories saved',
  'backup.button.backup': 'Backup',
  'backup.button.backup_share': 'Backup & Share',
  'backup.button.choose_file': 'Choose File',
  'backup.button.choose_file_restore': 'Choose File to Restore',
  'backup.tips.mobile_file_manager': "Use your device's file manager to find exported files",
  'backup.tips.mobile_file_picker': "For import, use your device's file picker",
  'backup.tips.ios': 'On iOS, files are saved to the Files app',
  'backup.tips.android': 'On Android, files are saved to Downloads folder',
  'backup.tips.desktop_downloads': 'Files will be saved to your Downloads folder',
  'backup.tips.desktop_location': 'You can choose a custom location during export',

  // ===== SAA home page (top of /saa) =====
  // title, subtitle, tab names and tab header lines mirror the English set in Admin → Programs → SAA.
  'saa_home.title': 'SAA - Sex Addicts Anonymous',
  'saa_home.subtitle':
    "Before coming to Sex Addicts Anonymous, many of us never knew that our problem had a name. All we knew was that we couldn't control our sexual behavior. For us, sex was a consuming way of life. Although the details of our stories were different, our problem was the same. We were addicted to sex.",
  'saa_home.read_more': 'read more',
  'saa_home.show_less': 'show less',
  'saa_home.loading': 'Loading…',
  'saa_home.retry': 'Retry',
  'saa_home.load_failed': 'Failed to load page configuration. Please try again later.',
  'saa_home.about.intro': 'This page provides everything you need for your SAA recovery journey. Use the tabs below to:',
  'saa_home.about.resources_label': 'Videos, Articles, Books, & Quotes',
  'saa_home.about.resources_body':
    'Explore recovery videos, articles, book recommendations, and quotes to support your journey.',
  'saa_home.about.steps_label': 'Work my steps',
  'saa_home.about.steps_body':
    'Track your progress through the 12 steps of recovery. Each step includes guided actions, recovery literature, and tools to help you work through the program systematically.',
  'saa_home.about.not_allied':
    'SAA is not allied with any sect, denomination, politics, organization, or institution. We do not wish to engage in any controversy, and we neither endorse nor oppose any causes. Our primary purpose is to stay sexually sober and help other sex addicts achieve sobriety.',
  'saa_home.about.membership':
    'The only requirement for membership is a desire to stop addictive sexual behavior. There are no dues or fees for SAA membership. We are self-supporting through our own contributions.',
  'saa_home.resources_english_note':
    'The videos, articles, books, and quotes in the {tab} tab are in English for now.',
  'saa_home.tab.resources': 'Resources',
  'saa_home.tab.meetings': 'Meetings',
  'saa_home.tab.resources_header': 'Explore recovery resources that you can watch and read.',
  'saa_home.tab.meetings_header': 'Meetings & Workshops\n(under construction)',
  'saa_home.loading_resources': 'Loading resources…',

  // ===== Resource panels (controls only; resource content stays in English) =====
  'panels.open': 'Open',
  'panels.learn_more': 'Learn More',
  'panels.no_link': 'No link available',
  'panels.no_video_url': 'No video URL',

  // ===== Document reader window (privacy policy, guides, readings) =====
  'content_reader.loading': 'Loading...',
  'content_reader.error_title': 'Error',
  'content_reader.load_failed': 'Failed to load content. Please try again.',
  'content_reader.untitled': 'Untitled',

  // ===== Step help panel (the ? button on step pages, Work my steps tab, Workshop page) =====
  // Use **double asterisks** around in-app control names to show them in bold.
  'step.help.menu.title': 'The menu (☰)',
  'step.help.menu.description': 'Tap the menu icon in the top-right corner of any page:',
  'step.help.menu.profile': 'Update your display name and phone, and manage sponsors in the Sponsorship tab.',
  'step.help.menu.invite': 'Invite a sponsor, sponsee, or friend to Recovery Commons.',
  'step.help.menu.feedback': 'Send suggestions, report a problem, or ask a question about the app.',
  'step.help.menu.help':
    'How step work, sharing, and the menu work in Recovery Commons — the same guide as the ? button on step pages.',
  'step.help.menu.start_over':
    'Archive your current step work and begin a fresh pass through the steps. Your prior work is kept in the archive.',
  'step.help.menu.backup':
    'Legacy export or restore of an encrypted backup file on your device. Most people use Save & start over instead.',
  'step.help.menu.about': 'Information about Recovery Commons.',
  'step.help.menu.privacy': 'How your data is protected and how sharing works.',
  'step.help.menu.sign_out': 'Sign out of your account.',
  'step.help.work.title': 'Working your steps in Recovery Commons',
  'step.help.work.description':
    'Recovery Commons is your private workspace for tracking 12-step work — inventories, prayers, plans, and progress. Your data saves automatically when you are signed in.',
  'step.help.work.step_open_steps':
    'From **SAA**, open **Work my steps** (the 12 Steps tab). Start with **Getting Started** and go from there.',
  'step.help.work.step_actions':
    'Each step has a list of actions. Check **Completed** when you finish an action. Completed actions collapse to keep the page manageable. Some actions include **Read Green Book**, **Read Guidance**, or **Watch Video** buttons, or fields where you enter your personal step work — questions, inventories, prayers, plans, and similar.',
  'step.help.work.step_help_button': 'Use the **?** help button at the top of any step page to reopen this guide.',
  'step.help.work.step_share':
    "When an action offers **Share with sponsor**, use it to preview or email a summary of your progress so you and your sponsor can review your work together. Add your sponsor's email in **Profile** (see the menu section below) to make sharing easier. Sharing is optional — you can check off the action once you have discussed your work, even if you do not send anything.",
  'step.help.work.step_field_help':
    'Use the **?** buttons inside forms when you need help with a specific field. You can uncheck an action if you marked it complete too soon. Share links expire after 7 days for privacy.',
  'step.help.steps_tab.title': 'The Work my steps tab',
  'step.help.steps_tab.description':
    "This tab is your checklist for working through SAA's 12 steps. Each card is a step — tap one to open it and work through its actions.",
  'step.help.steps_tab.step_start':
    'Start with **Getting Started**, then continue with **Step 1** through **Step 12** at your own pace.',
  'step.help.steps_tab.step_statuses':
    'When you are signed in, each card shows a status badge: **Not Started** (you have not begun), **In Progress** (you have completed at least one action), or **Completed** (all actions in that step are done).',
  'step.help.steps_tab.step_auto': 'Statuses update automatically as you work. Open any step to pick up where you left off.',
  'step.help.workshop.title': 'The Workshop page',
  'step.help.workshop.description':
    "The Workshop tab is your home for joining this workshop's Zoom meeting each week, connecting with the group, and opening your step work. **Workshop** in the page header (top of the app) brings you back here from anywhere in Recovery Commons.",
  'step.help.workshop.step_zoom':
    '**Join live · Zoom** shows the weekly workshop meeting time. Use **Open Zoom meeting** or **Copy link** to join.',
  'step.help.workshop.step_steps':
    '**Work my steps** opens your private SAA step checklist. The **?** button beside it opens this help.',
  'step.help.workshop.step_first_step':
    '**First Step presentation sign-up** — when open, sign up for a slot to share your First Step in a workshop session.',
  'step.help.workshop.step_participants':
    '**Workshop Participants** — phone list and contact options for members who confirmed attendance.',
  'step.help.workshop.step_share_invite': '**Share & invite** — link and text to invite others to the workshop.',
  'step.help.workshop.step_resources':
    '**Workshop Resources** — links shared by facilitators for this workshop (confirmed members).',
  'step.help.workshop.step_calendar': "**Workshop calendar** — where we are in the workshop and what's next.",
  'step.help.workshop.step_preferences':
    '**My preferences for this workshop** — phone list, email list, and availability to sponsor (visible only to workshop members).',
  'step.help.workshop.step_leave':
    '**Leave the workshop** — remove yourself from the roster or interest list. Your Recovery Commons step work is not affected.',
  'step.help.workshop_work.description':
    'Use Recovery Commons to track step work privately during the workshop. This is separate from the workshop Zoom meeting — it is your personal workspace.',
  'step.help.workshop_work.step_open_steps':
    'From the workshop page, click **Work my steps** to open **SAA** → **Work my steps** (the 12 Steps tab). Start with **Getting Started** and go from there.',
  'step.help.workshop_work.step_actions':
    'Each step has a list of actions. Check **Completed** when you finish an action. Progress saves automatically. Some actions include readings, guidance, or fields where you enter your personal step work — questions, inventories, prayers, plans, and similar.',
};
